/** Minimal REST client backed by fetch + the dev proxy in vite.config.ts. */
import type { SensorInstall } from "../stores/layoutStore";
import type { OccupancyCell } from "../scene/OccupancyLayer";
import type { RackVolume } from "../scene/rackVolume";

export interface Asset {
  id: string;
  siteId: string;
  warehouseId?: string;
  label: string;
  model?: string;
  typeCode?: string;
  modelUrl?: string;
  active: boolean;
  gatewayBaseUrl?: string;  // dialable edge URL — the capability signal for http transport only
  commandTransport?: string;                    // 'http' | 'mqtt' — server-owned command channel
  edgeBound?: boolean;      // an APPROVED gateway enrollment is bound to this asset
  edgeConfig?: Record<string, unknown> | null;  // server-owned edge config (jsonb overlay)
  typeModelUrl?: string;    // defaultModelUrl from asset_type (denormalized)
  typeModelScale?: number;  // model_scale from asset_type (denormalized)
}

export interface AssetType {
  typeCode: string;
  label: string;
  description?: string;
  defaultModelUrl?: string;
  modelScale?: number;
  /** ADR 288: all-in hourly rate for the type. Null = not priced. */
  hourCost?: number | null;
}

/** @deprecated Use Asset instead */
export type Forklift = Asset;

export interface OeeWindowRow {
  site_id: string;
  warehouse_id?: string;
  asset_id: string;
  /** Epoch millis (ADR 217). Was an ISO string built in the server's local zone,
   *  which put every window off by the server's UTC offset. */
  window_from: number;
  window_to: number;
  availability: number;
  performance: number;
  quality: number;
  oee: number;
  productive_moves: number;
  exception_moves: number;
  run_time_ms: number;
  planned_time_ms: number;
}

export interface AlertApiRecord {
  id: string;
  siteId: string;
  /** Null on inventory-derived alerts (ADR 282) — scope rides subject/detail. */
  assetId: string | null;
  code: string;
  /** Rule scope key (aisle node id, run id, impact hash) — ADR 282. */
  subject?: string | null;
  severity: 'INFO' | 'WARN' | 'CRIT';
  ts: string;
  status: 'open' | 'ack' | 'resolved';
  ackedBy?: string | null;
  ackedAt?: string | null;
  /** ADR 299 — when the rule re-measured the subject clear, or the subject was deleted. */
  resolvedAt?: string | null;
  resolvedReason?: 'condition_cleared' | 'subject_deleted' | null;
  detail?: Record<string, unknown> | null;
}

/** One measured factor; absent factors are omitted from ModularOee entirely. */
export interface ModularFactor {
  value: number;
  provider: string;
  tier: "true" | "proxy";
}

/** Response of /oee-modular (decisions.md#56/#61): graded by the weakest factor. */
export interface ModularOee {
  availability?: ModularFactor;
  performance?: ModularFactor;
  quality?: ModularFactor;
  basis: string[];
  grade: "OEE" | "OEE_ESTIMADO" | "INDICADORES";
  oee?: number;            // only when all three factors are present
  composite?: number;      // INDICADORES opt-in product of present factors
  compositeLabel?: string; // e.g. "A×Q"
}

const BASE = import.meta.env.VITE_API_BASE ?? "";

/**
 * Read the bearer token from the site store (set during Keycloak init) and
 * attach it on every API call. Imported lazily to avoid a circular dep —
 * siteStore imports listMySites from this module. Exported for the few
 * components that fetch binary assets directly (ADR 204: the Minimap's raw
 * raster fetch shipped without it and 401'd on every secured deployment).
 */
export function authHeaders(extra?: HeadersInit): HeadersInit {
  const t = (typeof window !== "undefined")
    ? ((window as any).__SITE_STORE_TOKEN as string | undefined)
    : undefined;
  return t ? { ...extra, Authorization: `Bearer ${t}` } : (extra ?? {});
}

async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: authHeaders(init?.headers),
  });
  if (!res.ok) throw new Error(await httpErrorMessage(res, path));
  return (await res.json()) as T;
}

/** Prefer the server's error body over the bare status line (ADR 144): backend
 *  validation puts the actionable reason in {"error": …} (controllers) or
 *  {"message": …} (Spring's default error body) — "400 Bad Request" alone sent
 *  operators hunting through service logs. */
async function httpErrorMessage(res: Response, path: string): Promise<string> {
  let detail = "";
  try {
    const text = (await res.text()).trim();
    if (text) {
      try {
        const body = JSON.parse(text) as Record<string, unknown>;
        detail = typeof body.error === "string" ? body.error
          : typeof body.message === "string" ? body.message : "";
      } catch {
        detail = text; // plain-text body (proxy/gateway errors)
      }
    }
  } catch { /* unreadable body — fall back to the status line */ }
  detail = detail.trim();
  if (detail.length > 300) detail = `${detail.slice(0, 300)}…`;
  const status = `${res.status} ${res.statusText}`.trim();
  return detail ? `${status} — ${detail}` : `${status} for ${path}`;
}

/** Build a "warehouseId=..." query suffix when defined, else empty. */
function whQuery(warehouseId?: string, leadingSep: "?" | "&" = "&"): string {
  return warehouseId ? `${leadingSep}warehouseId=${encodeURIComponent(warehouseId)}` : "";
}

export interface WarehouseTransform {
  origin_offset: [number, number, number];
  rotation_y_rad: number;
  scale: number;
}

export async function fetchWarehouseTransformById(siteId: string, warehouseId: string): Promise<WarehouseTransform | null> {
  const r = await fetch(
    `${BASE}/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/transform`,
    { headers: authHeaders(), credentials: "include" });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`fetchWarehouseTransformById: ${r.status}`);
  return r.json();
}

export async function putWarehouseTransformById(siteId: string, warehouseId: string, t: WarehouseTransform): Promise<WarehouseTransform> {
  const r = await fetch(
    `${BASE}/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/transform`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      credentials: "include",
      body: JSON.stringify(t),
    });
  if (!r.ok) throw new Error(`putWarehouseTransformById: ${r.status}`);
  return r.json();
}

export function uploadModel(
  siteId: string,
  typeCode: string,
  file: File,
  onProgress: (pct: number) => void
): Promise<{ series: string; url: string; uploadedAt: string }> {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("typeCode", typeCode);
    fd.append("siteId", siteId);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE}/v1/sites/${encodeURIComponent(siteId)}/models/upload`);
    const t = (typeof window !== "undefined")
      ? ((window as any).__SITE_STORE_TOKEN as string | undefined)
      : undefined;
    if (t) xhr.setRequestHeader("Authorization", `Bearer ${t}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
    };
    xhr.onload = () => {
      if (xhr.status === 201) resolve(JSON.parse(xhr.responseText) as { series: string; url: string; uploadedAt: string });
      else reject(new Error(`upload ${xhr.status}: ${xhr.responseText}`));
    };
    xhr.onerror = () => reject(new Error("network error"));
    xhr.send(fd);
  });
}

export function uploadWarehouseModel(
  siteId: string,
  warehouseId: string,
  file: File,
  onProgress: (pct: number) => void
): Promise<{ warehouseId: string; key: string; url: string; uploadedAt: string }> {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("file", file);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE}/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/model`);
    const t = (typeof window !== "undefined")
      ? ((window as any).__SITE_STORE_TOKEN as string | undefined)
      : undefined;
    if (t) xhr.setRequestHeader("Authorization", `Bearer ${t}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
    };
    xhr.onload = () => {
      if (xhr.status === 201) resolve(JSON.parse(xhr.responseText));
      else reject(new Error(`upload ${xhr.status}: ${xhr.responseText}`));
    };
    xhr.onerror = () => reject(new Error("network error"));
    xhr.send(fd);
  });
}

// ── Uploaded 2D floor plan (ADR 215) ─────────────────────────────────────────

/** A warehouse's uploaded 2D plan and its georeference. `resolution` is m/px and
 *  `origin` the twin-frame coordinates of the image's BOTTOM-LEFT pixel — same
 *  convention as the SLAM `map.yaml`, so the 2D views need one code path. */
export interface WarehousePlan {
  warehouseId: string;
  key: string;
  url: string;
  contentType: string;
  widthPx: number;
  heightPx: number;
  resolution: number;
  originX: number;
  originY: number;
  uploadedAt?: string;
}

/** The plan's real-world placement, as an operator states it: how wide the sheet
 *  is and where its bottom-left corner sits. Resolution is derived server-side. */
export interface WarehousePlanFrame {
  widthM: number;
  originX: number;
  originY: number;
}

/** `null` (not a throw) when the warehouse simply has no plan — the common case. */
export async function getWarehousePlan(
  siteId: string, warehouseId: string,
): Promise<WarehousePlan | null> {
  const res = await fetch(
    `${BASE}/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/plan`,
    { headers: authHeaders(), credentials: "include" });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`getWarehousePlan: ${res.status}`);
  return res.json();
}

export function uploadWarehousePlan(
  siteId: string,
  warehouseId: string,
  file: File,
  frame: WarehousePlanFrame,
  onProgress: (pct: number) => void,
): Promise<WarehousePlan> {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("widthM", String(frame.widthM));
    fd.append("originX", String(frame.originX));
    fd.append("originY", String(frame.originY));
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE}/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/plan`);
    const t = (typeof window !== "undefined")
      ? ((window as any).__SITE_STORE_TOKEN as string | undefined)
      : undefined;
    if (t) xhr.setRequestHeader("Authorization", `Bearer ${t}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
    };
    xhr.onload = () => {
      if (xhr.status === 201) resolve(JSON.parse(xhr.responseText) as WarehousePlan);
      else reject(new Error(`plan upload ${xhr.status}: ${xhr.responseText}`));
    };
    xhr.onerror = () => reject(new Error("network error"));
    xhr.send(fd);
  });
}

/** Re-aim the stored plan without re-uploading it (alignment is iterative). */
export async function putWarehousePlanFrame(
  siteId: string, warehouseId: string, frame: WarehousePlanFrame,
): Promise<WarehousePlan> {
  return getJson<WarehousePlan>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/plan/frame`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(frame) });
}

export async function deleteWarehousePlan(siteId: string, warehouseId: string): Promise<void> {
  const path = `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/plan`;
  const res = await fetch(`${BASE}${path}`, {
    method: "DELETE", headers: authHeaders(), credentials: "include",
  });
  if (!res.ok) throw new Error(await httpErrorMessage(res, path));
}

// ── Remote mapping ───────────────────────────────────────────────────────────

export interface MapBuild {
  id: string;
  // ready = staged preview awaiting the operator's keep/discard decision (decisions.md#77);
  // active = kept as the warehouse model; kept = camera-only; discarded = dropped.
  status: "mapping" | "building" | "ready" | "active" | "kept" | "discarded" | "failed" | "cancelled";
  mapName: string;
  warehouseId: string;
  mappingAssetId: string;
  modelUrl: string | null;
  buildProgressPct: number | null;  // camera's live mapping_progress during generation (decisions.md#51)
  areaM2: number | null;            // real mapped floor area (set when ready)
  error: string | null;
  packageUrl: string | null;        // archived raw map package (designer download, ADR 102)
  gridEdited?: boolean;             // a 2D-cleaned map-edited.zip exists (rebuilds use it)
  source?: "run" | "camera-import"; // provenance: walked run vs imported camera map (ADR 128)
}

/** Truthful camera state for a preview (V01/V02). `available:false` for no-inbound (mqtt) edges. */
export interface MapCameraState {
  available: boolean;
  activeMap?: string;
  isActive?: boolean;   // V01 — is THIS map the camera's active run map
  located?: boolean;    // V02 — is the camera currently localized
}

export async function startMapping(assetId: string, name: string): Promise<MapBuild> {
  return getJson<MapBuild>(`/v1/forklifts/${encodeURIComponent(assetId)}/mapping/start`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }),
  });
}

export async function stopMapping(buildId: string): Promise<MapBuild> {
  return getJson<MapBuild>(`/v1/maps/${encodeURIComponent(buildId)}/stop`, { method: "POST" });
}

export async function getMapBuild(buildId: string): Promise<MapBuild> {
  return getJson<MapBuild>(`/v1/maps/${encodeURIComponent(buildId)}`);
}

export async function listMapBuilds(siteId: string, warehouseId: string): Promise<MapBuild[]> {
  return getJson<MapBuild[]>(
    `/v1/maps?siteId=${encodeURIComponent(siteId)}&warehouseId=${encodeURIComponent(warehouseId)}`,
  );
}

/** A map on the equipment (camera-map manager, ADR 128/141). deployed:false =
 *  saved "background" map — needs a deploy before it can be imported/activated.
 *  Absent (pre-141 edge) means deployed. */
export interface CameraMap { name: string; active: boolean; deployed?: boolean; modifyTime?: string; }
export interface CameraMapsResult { available: boolean; maps: CameraMap[]; }

/** Every map on the equipment (deployed + saved), so one can be imported without a fresh walk.
 *  Saved maps import the same way — their zip is fetchable by name (ADR 141). */
export async function listCameraMaps(assetId: string): Promise<CameraMapsResult> {
  return getJson<CameraMapsResult>(`/v1/forklifts/${encodeURIComponent(assetId)}/mapping/camera-maps`);
}

/** Import an existing camera run map as a preview build (build/stage/preview rail, no walk). */
export async function importCameraMap(assetId: string, name: string): Promise<MapBuild> {
  return getJson<MapBuild>(`/v1/forklifts/${encodeURIComponent(assetId)}/mapping/import`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }),
  });
}

/** Outcome of pushing a map to the camera's localization (ADR 129). */
export interface SyncResult { ok: boolean; localized?: boolean; prior?: string | null; rolledBack?: boolean; error?: string; }

/** Push this build's map to the camera as its localization map + verify re-localization; the edge
 *  rolls back to the prior map on failure. Restarts the equipment. (ADR 129, slice 2.) */
export async function syncMapToCamera(buildId: string): Promise<SyncResult> {
  return getJson<SyncResult>(`/v1/maps/${encodeURIComponent(buildId)}/sync-to-camera`, { method: "POST" });
}

// Post-preview operator decisions (decisions.md#77).
/** Keep as active: switch the camera to this map + restart, and set it as the warehouse model. */
export async function activateMapBuild(buildId: string): Promise<MapBuild> {
  return getJson<MapBuild>(`/v1/maps/${encodeURIComponent(buildId)}/activate`, { method: "POST" });
}
/** Keep camera only: the SLAM map stays on the camera; not set active, not set as the model. */
export async function keepMapBuild(buildId: string): Promise<MapBuild> {
  return getJson<MapBuild>(`/v1/maps/${encodeURIComponent(buildId)}/keep`, { method: "POST" });
}
/** Discard: drop the staged preview. */
export async function discardMapBuild(buildId: string): Promise<MapBuild> {
  return getJson<MapBuild>(`/v1/maps/${encodeURIComponent(buildId)}/discard`, { method: "POST" });
}
/** Truthful camera state for the preview (V01 active map / V02 localized). */
export async function getMapCameraState(buildId: string): Promise<MapCameraState> {
  return getJson<MapCameraState>(`/v1/maps/${encodeURIComponent(buildId)}/camera-state`);
}

// ── 2D map cleanup rail (ADR 102) ────────────────────────────────────────────

/** The archived occupancy grid + frame metadata for the cleanup editor. */
export interface MapGrid {
  pgm: string;         // base64 of the whole .pgm file (binary P5)
  width: number;
  height: number;
  resolution: number;  // m/cell
  originX: number;
  originY: number;
  edited: boolean;     // serving map-edited.zip (not the camera original)
}

export async function getMapGrid(buildId: string): Promise<MapGrid> {
  return getJson<MapGrid>(`/v1/maps/${encodeURIComponent(buildId)}/grid`);
}

/** Save the cleaned raster (dimensions must match — the server enforces the frame invariant). */
export async function putMapGrid(buildId: string, pgm: Uint8Array): Promise<MapBuild> {
  const r = await fetch(`${BASE}/v1/maps/${encodeURIComponent(buildId)}/grid`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/octet-stream" }),
    body: pgm as unknown as BodyInit,
    credentials: "include",
  });
  if (!r.ok) throw new Error(`grid save failed: ${r.status}`);
  return r.json();
}

/** Re-extrude the GLB from the archived (possibly edited) package on the asset's edge. */
export async function rebuildMapBuild(buildId: string): Promise<MapBuild> {
  return getJson<MapBuild>(`/v1/maps/${encodeURIComponent(buildId)}/rebuild`, { method: "POST" });
}

/** Drop 2D edits: rebuilds use the immutable camera original again. */
export async function revertMapGrid(buildId: string): Promise<MapBuild> {
  return getJson<MapBuild>(`/v1/maps/${encodeURIComponent(buildId)}/grid`, { method: "DELETE" });
}

export interface SiteSummary {
  id: string; name: string; timezone?: string; lat?: number; lng?: number;
  hasRtls?: boolean; hasTelemetry?: boolean; hasInventory?: boolean; orgUnitId?: string;
}

export interface Warehouse { id: string; siteId: string; name: string; modelKey?: string; modelUrl?: string | null; createdAt?: string; }

export async function listMySites(): Promise<SiteSummary[]> {
  const r = await fetch(`${BASE}/v1/me/sites`, { headers: authHeaders(), credentials: "include" });
  if (!r.ok) return [];
  return r.json();
}

export async function listSites(): Promise<SiteSummary[]> {
  const r = await fetch(`${BASE}/v1/sites`, { headers: authHeaders(), credentials: "include" });
  if (!r.ok) return [];
  return r.json();
}

export async function createSite(body: {
  id: string; name: string; timezone: string; orgUnitId: string; lat?: number; lng?: number;
  hasRtls?: boolean; hasTelemetry?: boolean; hasInventory?: boolean;
}): Promise<SiteSummary> {
  return getJson<SiteSummary>(`/v1/sites`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}

export async function updateSite(siteId: string, body: {
  name?: string; timezone?: string; lat?: number | null; lng?: number | null;
  hasRtls?: boolean; hasTelemetry?: boolean; hasInventory?: boolean;
}): Promise<SiteSummary> {
  return getJson<SiteSummary>(`/v1/sites/${encodeURIComponent(siteId)}`, {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}

export async function deleteSite(siteId: string): Promise<void> {
  const r = await fetch(`${BASE}/v1/sites/${encodeURIComponent(siteId)}`, {
    method: "DELETE", headers: authHeaders(), credentials: "include",
  });
  if (!r.ok && r.status !== 404) throw new Error(`deleteSite: ${r.status}`);
}

// Newborn-edge default config (improvement02, decisions.md#80). `source` tells whether the
// returned template is the site's admin-curated override or the server's built-in.
export interface EdgeConfigDefault { template: Record<string, unknown>; source: "site" | "builtin"; }

export function getEdgeConfigDefault(siteId: string): Promise<EdgeConfigDefault> {
  return getJson<EdgeConfigDefault>(`/v1/sites/${encodeURIComponent(siteId)}/edge-config-default`);
}

export function setEdgeConfigDefault(siteId: string, template: unknown): Promise<EdgeConfigDefault> {
  return getJson<EdgeConfigDefault>(`/v1/sites/${encodeURIComponent(siteId)}/edge-config-default`, {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(template),
  });
}

export function clearEdgeConfigDefault(siteId: string): Promise<EdgeConfigDefault> {
  return getJson<EdgeConfigDefault>(`/v1/sites/${encodeURIComponent(siteId)}/edge-config-default`, {
    method: "DELETE",
  });
}

export async function listWarehouses(siteId: string): Promise<Warehouse[]> {
  return getJson<Warehouse[]>(`/v1/sites/${encodeURIComponent(siteId)}/warehouses`);
}

export async function createWarehouse(siteId: string, body: { name: string }): Promise<Warehouse> {
  return getJson<Warehouse>(`/v1/sites/${encodeURIComponent(siteId)}/warehouses`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}

export async function updateWarehouse(siteId: string, id: string, body: { name?: string }): Promise<Warehouse> {
  return getJson<Warehouse>(`/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(id)}`, {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}

export async function deleteWarehouse(siteId: string, id: string): Promise<void> {
  const r = await fetch(`${BASE}/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(id)}`, {
    method: "DELETE", headers: authHeaders(), credentials: "include",
  });
  if (!r.ok && r.status !== 404) throw new Error(`deleteWarehouse: ${r.status}`);
}

/** Warehouse traffic/safety zones (ZoneController). Edited on Estrutura → Zonas (ADR 403);
 *  the /live ZonesLayer draws them (ADR 143). Polygon arrives as a real [[x,y]] array — the controller
 *  parses the jsonb text server-side (ADR 123 pattern). */
export interface ZoneRow {
  id: string;
  siteId: string;
  warehouseId: string | null;
  kind: string;
  mapId: string | null;
  polygon: number[][] | null;
  maxSpeedMps: number | null;
  directionRad: number | null;
  /** The structure node a derived aisle zone came from (ADR 213); null on
   *  hand-authored zones. Matches AisleMetrics.id — the join the /live zone
   *  inspection dock rides (ADR 278). */
  nodeId: string | null;
}

export async function listZones(siteId: string, warehouseId?: string): Promise<ZoneRow[]> {
  const q = warehouseId ? `?warehouseId=${encodeURIComponent(warehouseId)}` : "";
  return getJson<ZoneRow[]>(`/v1/sites/${encodeURIComponent(siteId)}/zones${q}`);
}

/** ADR 403: the hand-authored subset of `zone.kind`; `aisle` is derived by the graph. */
export const EDITABLE_ZONE_KINDS = ["dock", "staging", "restricted", "pedestrian", "one_way"] as const;
export type EditableZoneKind = typeof EDITABLE_ZONE_KINDS[number];
export const isDerivedZone = (id: string): boolean => id.startsWith("az:");

export interface ZoneUpsert {
  id: string;
  kind: EditableZoneKind;
  polygon: number[][];
  maxSpeedMps: number | null;
  directionRad: number | null;
}
export interface ZoneBatchRequest { upsert: ZoneUpsert[]; delete: string[]; }
export interface ZoneBatchResult { created: number; updated: number; deleted: number; }

/** A refused batch: `message` is the server's reason, `zoneId` the zone it names (null when none). */
export class ZoneBatchError extends Error {
  constructor(message: string, readonly zoneId: string | null, readonly status: number) {
    super(message);
    this.name = "ZoneBatchError";
  }
}

/** The editor's whole diff in one transaction (ADR 403). A 400/404/409 answers
 *  `{"error": reason, "id": zoneId}` (ZoneController's handler) and is thrown as a
 *  ZoneBatchError; any other failure body (gateway 401, Spring's 403) keeps the
 *  generic ADR 144 message. */
export async function batchZones(siteId: string, warehouseId: string, req: ZoneBatchRequest): Promise<ZoneBatchResult> {
  const path = `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/zones/batch`;
  const res = await fetch(`${BASE}${path}`, {
    method: "PUT",
    credentials: "include",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(req),
  });
  if (res.ok) return (await res.json()) as ZoneBatchResult;
  const body = await res.clone().json().catch(() => null) as { error?: unknown; id?: unknown } | null;
  // The `id` key (even null) marks ZoneController's body; Spring's default error body
  // also has a string `error` ("Forbidden"), which is not a reason.
  if (body && "id" in body && typeof body.error === "string" && body.error.trim()) {
    throw new ZoneBatchError(body.error.trim(), typeof body.id === "string" ? body.id : null, res.status);
  }
  throw new Error(await httpErrorMessage(res, path));
}

// ── Structure graph (ADR 164/168) ────────────────────────────────────────────
/** A node in the editable warehouse structure tree. `source` is provenance:
 *  `derived` (address grammar) is rebuilt on every backfill; `manual` (operator
 *  edit) and `imported` (iC3 sync) survive it. Curatable fields — name, purpose,
 *  rackType, side, heights, storable/occupiable — flip the node to `manual`. */
export interface StructureNode {
  id: string;
  kind: "zone" | "aisle" | "rack" | "level" | "space";
  purpose: string | null;
  code: string | null;
  name: string | null;
  path: string;
  rackType: string | null;
  side: string | null;
  elevationM: number | null;
  clearHeightM: number | null;
  storable: boolean;
  occupiable: boolean;
  source: "derived" | "manual" | "imported";
  locationCount: number;
  children: StructureNode[];
}

/** Partial curation patch — omitted fields are left unchanged server-side. */
export interface StructureNodePatch {
  name?: string;
  purpose?: string | null;
  rackType?: string | null;
  side?: string | null;
  elevationM?: number | null;
  clearHeightM?: number | null;
  storable?: boolean;
  occupiable?: boolean;
}

export async function getStructure(siteId: string, warehouseId: string): Promise<StructureNode[]> {
  return getJson<StructureNode[]>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure`);
}

/** (Re)derive the graph from active locations; preserves `manual` nodes. */
/** `aislePattern` (ADR 188, optional): an address-grammar regex with named groups
 *  `aisle`/`level`/`space`, stored on every map of the warehouse and used to re-derive
 *  labels + levels — the self-heal for maps imported before their grammar was known. */
export async function backfillStructure(
  siteId: string, warehouseId: string, aislePattern?: string,
): Promise<{ nodes: number; relabeled: number; leveled: number }> {
  const qs = aislePattern ? `?aislePattern=${encodeURIComponent(aislePattern)}` : "";
  return getJson<{ nodes: number; relabeled: number; leveled: number }>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/backfill${qs}`,
    { method: "POST" });
}

/** ADR 213: derive one real aisle zone per structure aisle — the convex hull of the
 *  aisle's member-location footprints. Idempotent; stale derived zones are removed. */
export async function deriveAisleZones(
  siteId: string, warehouseId: string,
): Promise<{ created: number; updated: number; deleted: number; aisles: number }> {
  return getJson<{ created: number; updated: number; deleted: number; aisles: number }>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/derive-aisle-zones`,
    { method: "POST" });
}

/** Curate one node. The id is slash-heavy, so it rides as a query param, not a
 *  path segment. Returns the node's own fields (no children/count). */
export async function updateStructureNode(
  siteId: string, warehouseId: string, nodeId: string, patch: StructureNodePatch,
): Promise<Omit<StructureNode, "children" | "locationCount">> {
  return getJson<Omit<StructureNode, "children" | "locationCount">>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}`
      + `/structure/node?nodeId=${encodeURIComponent(nodeId)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch) });
}

/** A new manually-authored node (ADR 173, S8). `parentId` null creates a root zone; `code` is
 *  required (the path-segment natural key); storable/occupiable default from purpose when omitted. */
export interface StructureNodeCreate {
  parentId: string | null;
  kind: StructureNode["kind"];
  code: string;
  purpose?: string | null;
  name?: string | null;
  rackType?: string | null;
  side?: string | null;
  elevationM?: number | null;
  clearHeightM?: number | null;
  storable?: boolean | null;
  occupiable?: boolean | null;
}

/** Author a new manual node the address grammar can't derive — a rack, a dock, an office
 *  (ADR 173, S8). Returns the node's own fields (no children/count). */
export async function createStructureNode(
  siteId: string, warehouseId: string, req: StructureNodeCreate,
): Promise<Omit<StructureNode, "children" | "locationCount">> {
  return getJson<Omit<StructureNode, "children" | "locationCount">>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/node`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(req) });
}

/** Re-point a node under a new parent (null = promote to root) — the "put levels in this rack"
 *  primitive (ADR 173, S8). Both ids are slash-heavy, so they ride as query params. */
export async function reparentStructureNode(
  siteId: string, warehouseId: string, nodeId: string, parentId: string | null,
): Promise<Omit<StructureNode, "children" | "locationCount">> {
  const q = new URLSearchParams({ nodeId });
  if (parentId != null) q.set("parentId", parentId);
  return getJson<Omit<StructureNode, "children" | "locationCount">>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/node/parent?${q.toString()}`,
    { method: "PATCH" });
}

/** Delete a manually-authored node (ADR 173, S8). Children re-point to its parent; bound
 *  locations unbind. Returns 204 — no body, so it can't ride getJson. */
export async function deleteStructureNode(siteId: string, warehouseId: string, nodeId: string): Promise<void> {
  const path = `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}`
    + `/structure/node?nodeId=${encodeURIComponent(nodeId)}`;
  const res = await fetch(`${BASE}${path}`, { method: "DELETE", credentials: "include", headers: authHeaders() });
  if (!res.ok) throw new Error(await httpErrorMessage(res, path));
}

/** ADR 170 (inferred 3D): the warehouse's bound rack Spaces as extruded prisms
 *  (warehouse-frame footprint + base + clear height), for the scene's rack-volumes
 *  layer. `RackVolume` lives in the scene module and is re-exported here so the API
 *  and the layer can't drift (as OccupancyCell does). Run-independent — reads the
 *  canonical structure, not an inventory run. */
export type { RackVolume };
export async function getStructureVolumes(
  siteId: string, warehouseId: string,
): Promise<{ volumes: RackVolume[] }> {
  return getJson<{ volumes: RackVolume[] }>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/volumes`);
}

/** ADR 227: cheap counts of what the warehouse actually holds — the layer panel gates
 *  its rows on these rather than fetching 15k prisms to decide whether to draw a
 *  checkbox. `rackVolumes` is the count of bins that resolve to a rack prism. */
export interface StructureSummary {
  locations: number;
  withFootprint: number;
  boundToStructure: number;
  structureNodes: number;
  rackVolumes: number;
  /** ADR 322: of those prisms, how many sit where the nominal FORMULA says rather than
   *  where anybody measured. A standing number, because the per-bin badge only answers
   *  the question once you already suspect it. */
  elevationEstimated?: number;
  /** ADR 231: the warehouse's remembered address grammar, or null before the
   *  first import that supplied one. */
  addressPattern: string | null;
}

export async function getStructureSummary(
  siteId: string, warehouseId: string,
): Promise<StructureSummary> {
  return getJson<StructureSummary>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/summary`);
}

/** ADR 226: the warehouse's fallback rack geometry plus the per-level curation.
 *  `clearHeightM`/`levelPitchM` null = the global default (echoed as `default*`). */
export interface HeightsConfig {
  nominal: {
    clearHeightM: number | null;
    levelPitchM: number | null;
    defaultClearHeightM: number;
    defaultLevelPitchM: number;
    /** ADR 245: null = the legacy beam-free elevation stack; the default is what
     *  the next heights import applies (and stamps) when nothing is set. */
    beamHeightM: number | null;
    defaultBeamHeightM: number;
  };
  levels: { level: number; nodes: number; clearHeightM: number | null; elevationM: number | null }[];
}

export async function getHeightsConfig(
  siteId: string, warehouseId: string,
): Promise<HeightsConfig> {
  return getJson<HeightsConfig>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/heights`);
}

/** ADR 226: set the warehouse nominal. Nulls clear it back to the global default. */
export async function putNominalHeights(
  siteId: string, warehouseId: string,
  body: { clearHeightM: number | null; levelPitchM: number | null },
): Promise<HeightsConfig> {
  return getJson<HeightsConfig>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/heights/nominal`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
}

/** ADR 245: set the rack beam height; the server shifts every stored leveled
 *  elevation to embed it. Null resets to the platform default. */
export async function putBeamHeight(
  siteId: string, warehouseId: string, beamHeightM: number | null,
): Promise<HeightsConfig & { shifted: number }> {
  return getJson<HeightsConfig & { shifted: number }>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/heights/beam`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ beamHeightM }) });
}

/** ADR 226: write clear heights per LEVEL across the warehouse, or one aisle.
 *  Elevations stack from the clear heights unless supplied. */
export async function putLevelHeights(
  siteId: string, warehouseId: string,
  body: { aisle?: string | null; levels: { level: number; clearHeightM: number | null; elevationM: number | null }[] },
): Promise<{ levelsUpdated: number; aislesTouched: number }> {
  return getJson<{ levelsUpdated: number; aislesTouched: number }>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/heights/levels`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
}

/** Occupancy summed over a node's subtree for one run (ADR 171, S6). Denominator is the
 *  occupiable bound leaves, so occupancyPct is null where a subtree has nothing occupiable. */
export interface StructureOccupancy {
  occupiableSpaces: number;
  occupiedSpaces: number;
  emptySpaces: number;
  notScannedSpaces: number;
  occupancyPct: number | null;
  /** ADR 292 — how many of the subtree's OCCUPIED bins the iC3 rack-height sensor measured.
   *  The sensor samples a minority of reads (~25 % on the JSL and Flex walks), so this is
   *  the sample `verticalFillPct` rests on and never leaves its side on screen. */
  verticalMeasuredSpaces: number;
  /** Mean share of a slot's clear height its load uses, over those measured bins, 0–100.
   *  Null where none were measured — never 0, which would claim flat pallets. */
  verticalFillPct: number | null;
  /** ADR 293 — of `verticalMeasuredSpaces`, how many were divided by a clear height the TWIN
   *  states (per-location measurement, curated level, configured nominal) because the kit
   *  reported no occupancy of its own. Non-zero means the mean is partly an assumption about
   *  the rack, and every surface says so. */
  verticalDerivedSpaces: number;
}

/** A structure node carrying its subtree occupancy rollup: the same node fields as
 *  {@link StructureNode} (minus the run-independent locationCount) plus {@link StructureOccupancy}. */
export interface StructureOccupancyNode extends Omit<StructureNode, "locationCount" | "children"> {
  occupancy: StructureOccupancy;
  children: StructureOccupancyNode[];
}

/** One run's occupancy grouped by the structure graph — the structure-aware counterpart to
 *  {@link getInventoryAisleCoverage}. Run-scoped: pass the run whose occupancy to roll up.
 *
 *  `depth` truncates the tree (roots = 0) without changing any number, because every node's
 *  counts are summed over its whole subtree before serialization (ADR 276). **Pass it unless
 *  you actually render the leaves**: the untruncated JSL response is 6 MB of individual
 *  spaces, and a caller that only sums the roots pays all of it. */
export async function getStructureOccupancy(
  siteId: string, warehouseId: string, runId: string, depth?: number,
): Promise<{ nodes: StructureOccupancyNode[] }> {
  return getJson<{ nodes: StructureOccupancyNode[] }>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}`
      + `/structure/occupancy?runId=${encodeURIComponent(runId)}`
      + (depth == null ? "" : `&depth=${depth}`));
}

// ── per-aisle metrics (ADR 276) ──────────────────────────────────────────────
/** Null on an aisle the comparison never reached — an aisle no WMS row and no reading
 *  mentions has not been measured at 0 % agreement, it has not been measured. */
export interface AisleDivergence {
  lines: number;
  matched: number;
  qtyMismatch: number;
  missingInIc3: number;
  unexpectedInIc3: number;
  agreementPct: number;
}
/** Age is the WMS FIFO date against the snapshot's asOf (ADR 194) — time since the pallet
 *  entered the BUILDING, never time in this slot, and never a scan timestamp. `lines` counts
 *  only rows that carry a date, so the average is not dragged toward zero by undated ones.
 *  Null where the snapshot dates nothing in this aisle. */
export interface AisleAging {
  lines: number;
  avgAgeDays: number;
  over90Days: number;
}
export interface AisleMetrics {
  id: string;
  code: string | null;
  occupancy: StructureOccupancy;
  divergence: AisleDivergence | null;
  aging: AisleAging | null;
}
export interface AisleMetricsReport {
  runId: string | null;
  aisles: AisleMetrics[];
  /** Divergence rows whose address binds to no aisle — reported, not dropped, so the
   *  breakdown cannot read as covering a warehouse it does not cover. */
  unassignedLines: number;
  wmsSnapshot: boolean;
}

/** One row per aisle: occupancy × iC3-vs-WMS divergence × FIFO age, joined server-side on
 *  the address → aisle binding the structure graph owns (ADR 276). Not derivable in the
 *  client: JSL addresses look like `06J0544`, so no prefix rule recovers the aisle. */
export async function getStructureAisleMetrics(
  siteId: string, warehouseId: string, runId: string,
): Promise<AisleMetricsReport> {
  return getJson<AisleMetricsReport>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}`
      + `/structure/aisle-metrics?runId=${encodeURIComponent(runId)}`);
}

/** One slotting insight for a run (ADR 172, S7). `type` selects which metric fields carry:
 *  stray_load has `address`; consolidation has occupied/empty/reclaimable/pct; putaway_capacity
 *  has `emptyStorableSpaces`. */
export interface StructureSlottingInsight {
  type: "stray_load" | "consolidation" | "putaway_capacity";
  severity: "high" | "medium" | "info";
  nodeId: string;
  kind: string;
  code: string | null;
  name: string | null;
  path: string;
  purpose: string | null;
  address?: string;
  occupiedSpaces?: number;
  emptySpaces?: number;
  reclaimableSpaces?: number;
  occupancyPct?: number;
  emptyStorableSpaces?: number;
}

/** Headline totals for a run's slotting insights (full counts; the ranked lists are capped). */
export interface StructureSlottingSummary {
  strayLoads: number;
  consolidationCandidates: number;
  emptyStorableSpaces: number;
  /** ADR 314 (optional: absent on pre-314 backends). Confirmed-occupied count — the
   *  other half of the free-% denominator — and share-adjusted floor areas (a stacked
   *  position owns footprint ÷ stack size), priced by `MovementRate.sqmMonthCost`. */
  occupiedStorableSpaces?: number;
  emptyAreaSqm?: number;
  occupiedAreaSqm?: number;
}

/** One run's slotting insights over the structure graph (ADR 172, S7): what to move (stray
 *  loads), what to consolidate (low-occupancy storable subtrees) and where stock fits today
 *  (known-empty storable capacity). Run-scoped. */
export async function getStructureSlotting(
  siteId: string, warehouseId: string, runId: string,
): Promise<{ summary: StructureSlottingSummary; insights: StructureSlottingInsight[] }> {
  return getJson<{ summary: StructureSlottingSummary; insights: StructureSlottingInsight[] }>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}`
      + `/structure/slotting?runId=${encodeURIComponent(runId)}`);
}

/** One iC3 address mapped to its structure coordinates (ADR 174, S9). Null tiers are absent from
 *  that address's chain. */
export interface StructureBinding {
  address: string;
  nodeId: string;
  zone: string | null;
  aisle: string | null;
  rack: string | null;
  level: string | null;
  space: string | null;
  purpose: string | null;
}

/** A node in the twin → iC3 export: the as-designed record incl. parentId, so the consumer can
 *  rebuild the tree (ADR 174, S9). */
export interface StructureExportNode {
  id: string;
  parentId: string | null;
  kind: string;
  purpose: string | null;
  code: string | null;
  name: string | null;
  path: string;
  rackType: string | null;
  side: string | null;
  elevationM: number | null;
  clearHeightM: number | null;
  storable: boolean;
  occupiable: boolean;
  source: string;
}

/** The twin → iC3 structure export (ADR 174, S9): the canonical graph plus the location→Space
 *  binding, so iC3 can enrich its flat maps with the structure the twin owns. */
export interface StructureExport {
  warehouseId: string;
  nodes: StructureExportNode[];
  bindings: StructureBinding[];
}

export async function getStructureExport(siteId: string, warehouseId: string): Promise<StructureExport> {
  return getJson<StructureExport>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/export`);
}

/** ADR 186: the per-position measured-heights file ("Tamanho do Local" .xlsx or its CSV
 *  export) — sets clear heights, backfills levels, stacks elevations. */
export interface HeightsImportSummary {
  fileRows: number;
  matchedAddresses: number;
  matchedLocations: number;
  unmatchedAddresses: number;
  unmatchedSample: string[];
  elevationsSet: number;
  elevationSkipped: number;
  rejects: { line: string; reason: string }[];
}

export function importPositionHeights(
  siteId: string, warehouseId: string, file: File,
): Promise<HeightsImportSummary> {
  const fd = new FormData();
  fd.append("file", file);
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/structure/heights-import`,
    { method: "POST", body: fd });
}

export async function getWarehouseModelUrl(siteId: string, warehouseId: string): Promise<{ url: string } | null> {
  const r = await fetch(
    `${BASE}/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/model`,
    { headers: authHeaders(), credentials: "include" });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`getWarehouseModelUrl: ${r.status}`);
  return r.json();
}

// ── OEE rule (per-warehouse shift calendar, ADR 109) ─────────────────────────
/** Shifts are local-time windows in the site timezone; end ≤ start wraps past
 *  midnight; an empty list means 24/7. `idealCycleS` is the ideal cycle time
 *  paired with the calendar for the OEE performance factor. */
export interface OeeRule {
  warehouseId: string;
  siteId: string;
  idealCycleS: number;
  shifts: ShiftWindow[];
}

// ── movement rates + fleet report (ADR 288, movement-cost slice 1) ───────────
/** Warehouse money + cycle constants. Null hour cost = not priced (reports
 *  return cost: null, never a guess); null speeds = calibrate from telemetry. */
export interface MovementRate {
  warehouseId: string;
  siteId: string;
  defaultHourCost: number | null;
  /** ADR 314: rent + operation per m² per month. Null = not configured — no cost shown. */
  sqmMonthCost?: number | null;
  currency: string;
  pickS: number;
  dropS: number;
  liftSpeedMps: number;
  loadedSpeedMps: number | null;
  emptySpeedMps: number | null;
}

export async function getMovementRate(siteId: string, warehouseId: string): Promise<MovementRate> {
  return getJson<MovementRate>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/movement-rates`,
  );
}

export async function putMovementRate(
  siteId: string, warehouseId: string, body: Partial<Omit<MovementRate, "warehouseId" | "siteId">>,
): Promise<MovementRate> {
  return getJson<MovementRate>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/movement-rates`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
  );
}

/** One forklift's period aggregates. Percentages are 0–100; null = not
 *  measurable (no moving time / no rate), never zero. */
export interface FleetReportAsset {
  assetId: string;
  label: string | null;
  typeCode: string | null;
  registered: boolean;
  loadedMs: number;
  emptyMs: number;
  idleMs: number;
  linkDownMs: number;
  runMs: number;
  distanceM: number;
  distanceLoadedM: number;
  distanceEmptyM: number;
  moves: number;
  avgSpeedMps: number | null;
  emptyTimePct: number | null;
  emptyDistancePct: number | null;
  utilizationPct: number | null;
  hourCost: number | null;
  cost: number | null;
}

export interface FleetReport {
  from: number;
  to: number;
  warehouseId: string;
  currency: string;
  plannedMs: number;
  /** ADR 291: fleet move-time vs shift capacity per local shift-day. */
  shiftDays?: { date: string; moveMs: number; shiftMs: number }[];
  assets: FleetReportAsset[];
  fleet: {
    assets: number;
    runMs: number;
    distanceM: number;
    moves: number;
    emptyTimePct: number | null;
    emptyDistancePct: number | null;
    /** ADR 289: measured fleet travel speeds — the /admin/rates calibration hint. */
    avgLoadedSpeedMps?: number | null;
    avgEmptySpeedMps?: number | null;
    cost: number | null;
    costPerMove: number | null;
    unpricedAssets: number;
  };
}

/** ADR 289/290: the relocation what-if. Stateless — request in, numbers out. */
export interface RelocationSim {
  runId: string;
  destinationZoneId: string;
  selected: number;
  moves: number;
  shortfall: number;
  unroutable: number;
  freeSlotsInZone: number;
  routing: "aisles" | "direct";
  loadedDistanceM: number;
  emptyDistanceM: number;
  totalDistanceM: number;
  forkliftHours: number;
  cost: number | null;
  currency: string;
  costPerMove: number | null;
  speeds: { loadedMps: number; emptyMps: number; configured: boolean };
  makespan: { forklifts: number; hoursPerForklift: number; shiftDays: number | null };
  moveList: { from: string; to: string; product: string | null; expiryDate: string | null; distanceM: number; seconds: number }[];
  routes: [number, number][][];
}

export async function simulateRelocation(
  siteId: string, warehouseId: string,
  body: {
    selection: { expiryBefore?: string | null; product?: string | null; addresses?: string[] | null };
    destinationZoneId: string;
    forklifts?: number;
    runId?: string | null;
  },
): Promise<RelocationSim> {
  return getJson<RelocationSim>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/simulations/relocation`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
  );
}

/** ADR 305: one policy's totals. Both blocks come from the same task lists. */
export interface TaskCycleBlock {
  pallets: number;
  loadedDistanceM: number;
  emptyDistanceM: number;
  totalDistanceM: number;
  forkliftHours: number;
  secondsPerPallet: number | null;
  cost: number | null;
  costPerPallet: number | null;
  emptyDistancePct: number | null;
}

/** ADR 305: the task-interleaving what-if. Stateless, like the relocation one. */
export interface TaskMixSim {
  runId: string;
  dockZoneId: string;
  routing: "aisles" | "direct";
  unroutable: number;
  tasks: { putaway: number; retrieval: number; total: number };
  speeds: { loadedMps: number; emptyMps: number; configured: boolean };
  currency: string;
  maxInterleaveM: number;
  singleCommand: TaskCycleBlock;
  dualCommand: TaskCycleBlock & {
    pairs: number;
    achievedInterleaveRate: number;
    unpaired: {
      putaway: number;
      retrieval: number;
      reason: "rateCap" | "streamImbalance" | "tooFar" | null;
    };
  };
  delta: {
    secondsPerPalletPct: number | null;
    forkliftHours: number;
    cost: number | null;
    emptyDistancePctPoints: number | null;
  };
  /** `basis` is always "average" — a fleet is sized by its PEAK, so this is
   *  capacity arithmetic, never a staffing plan (ADR 305 / ADR 291). */
  fleet: {
    current: number;
    equivalent: number;
    basis: string;
    shiftHours: number | null;
    singleCommandShiftDays: number | null;
    dualCommandShiftDays: number | null;
  };
}

export async function simulateTaskMix(
  siteId: string, warehouseId: string,
  body: {
    dockZoneId: string;
    putaway?: { count?: number | null } | null;
    retrieval?: { count?: number | null } | null;
    maxInterleaveM?: number | null;
    interleaveRateCap?: number | null;
    forklifts?: number;
    runId?: string | null;
  },
): Promise<TaskMixSim> {
  return getJson<TaskMixSim>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/simulations/task-mix`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
  );
}

export async function getFleetReport(
  siteId: string, warehouseId: string, fromMs: number, toMs: number,
): Promise<FleetReport> {
  return getJson<FleetReport>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/fleet-report?from=${fromMs}&to=${toMs}`,
  );
}

export async function getOeeRule(siteId: string, warehouseId: string): Promise<OeeRule> {
  return getJson<OeeRule>(
    `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/oee-rules`,
  );
}

export async function putOeeRule(
  siteId: string, warehouseId: string, body: { idealCycleS: number; shifts: ShiftWindow[] },
): Promise<OeeRule> {
  const r = await fetch(
    `${BASE}/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/oee-rules`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      credentials: "include",
      body: JSON.stringify(body),
    },
  );
  if (!r.ok) {
    let msg = `${r.status}`;
    try { msg = (await r.json()).error ?? msg; } catch { /* non-JSON body */ }
    throw new Error(msg);
  }
  return r.json();
}

// ── Tenants / org-units (ADR 110/167) ────────────────────────────────────────
export interface OrgUnit { id: string; parentId?: string | null; type: string; name: string; path: string; }

export async function listOrgUnits(): Promise<OrgUnit[]> {
  return getJson<OrgUnit[]>(`/v1/org-units`);
}

/** Org-units the caller may administer (ADR 177) — global-admin sees all, a tenant
 *  admin sees their org-unit-admin subtree, everyone else []. Drives grant-aware nav. */
export async function listMyOrgUnits(): Promise<OrgUnit[]> {
  const r = await fetch(`${BASE}/v1/me/org-units`, { headers: authHeaders(), credentials: "include" });
  if (!r.ok) return [];
  return r.json();
}

export async function createOrgUnit(body: { type: string; name: string; parentId?: string }): Promise<OrgUnit> {
  return getJson<OrgUnit>(`/v1/org-units`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}

// ── Access grants (hierarchical tenancy, ADR 110) ────────────────────────────
export interface AccessGrant {
  id: string;
  subject: string;
  scopeType: "org_unit" | "site";
  scopeId: string;
  role: string;
}

/** Raw grant rows for a subject (site grants + any org_unit grants). */
export async function listSubjectGrants(subject: string): Promise<AccessGrant[]> {
  return getJson<AccessGrant[]>(`/v1/grants?subject=${encodeURIComponent(subject)}`);
}

/** Grant a subject a role on a scope — used to make a subject a tenant admin (org_unit/admin). */
export async function createGrant(body: {
  subject: string; scopeType: "org_unit" | "site"; scopeId: string; role: string;
}): Promise<AccessGrant> {
  return getJson<AccessGrant>(`/v1/grants`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}

/** All access grants (global-admin only) — used to show each tenant's admin (ADR 180). */
export async function listAllGrants(): Promise<AccessGrant[]> {
  return getJson<AccessGrant[]>(`/v1/grants`);
}

/** Set (replace) a tenant's admin — global-admin only (ADR 180). Empty subject clears it.
 *  Hard-scoped to org_unit/admin server-side, so it can never touch the global-admin role. */
export async function setTenantAdmin(orgUnitId: string, subject: string): Promise<void> {
  await getJson<{ orgUnitId: string; subject: string }>(`/v1/org-units/${encodeURIComponent(orgUnitId)}/admin`, {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ subject }),
  });
}

/** Read-only IdP account lookup so the access UI can grant by email instead of a raw sub (ADR 176). */
export interface IdpUser { sub: string; email: string; name: string; }
export async function searchIdpUsers(q: string): Promise<IdpUser[]> {
  const query = q.trim();
  if (query.length < 2) return [];
  return getJson<IdpUser[]>(`/v1/idp/users?q=${encodeURIComponent(query)}`);
}

/** Resolve one account by sub → email/name for display (ADR 181); null if unknown/unavailable. */
export async function resolveIdpUser(sub: string): Promise<IdpUser | null> {
  const r = await fetch(`${BASE}/v1/idp/users/${encodeURIComponent(sub)}`, { headers: authHeaders(), credentials: "include" });
  if (!r.ok) return null;
  return r.json();
}

/** The sites a subject actually resolves to (site grants ∪ org_unit-expanded sites). */
export async function getResolvedSites(subject: string): Promise<{ subject: string; siteIds: string[] }> {
  return getJson(`/v1/subjects/${encodeURIComponent(subject)}/sites`);
}

/** Set a subject's site access wholesale — replaces their site grants with this set. */
export async function setSubjectSites(
  subject: string, siteIds: string[], role: string,
): Promise<{ subject: string; role: string; sites: string[] }> {
  const r = await fetch(`${BASE}/v1/subjects/${encodeURIComponent(subject)}/sites`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify({ siteIds, role }),
  });
  if (!r.ok) {
    let msg = `${r.status}`;
    try { msg = (await r.json()).error ?? msg; } catch { /* non-JSON */ }
    throw new Error(msg);
  }
  return r.json();
}

// ── History API ──────────────────────────────────────────────────────────────

export interface TrackPoint { ts: number; x: number; y: number; yaw: number; }
export interface AssetTrack  { asset_id: string; points: TrackPoint[]; }
export interface HeatmapCell  { x: number; z: number; count: number; }
export interface BufferCell   { gx: number; gz: number; buffered_pct: number; buffered: number; samples: number; }
export interface ConnSegment  { type: string; fromMs: number; toMs: number; }
export interface ConnAsset    { id: string; segments: ConnSegment[]; }
export interface ConnData     { assets: ConnAsset[]; fromMs: number; toMs: number; }
/** start_ts/end_ts are epoch millis (ADR 217) — `new Date(ms)` reads them directly. */
export interface Disruption   { asset_id: string; start_ts: number; end_ts: number; duration_s: number; }
/** Weekday codes as the API spells them (ADR 257), in weekday order. */
export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type DayCode = (typeof WEEKDAYS)[number];

/** `days` absent means every day — the shape of every calendar written before ADR 257. */
export interface ShiftWindow  { start: string; end: string; days?: DayCode[]; }

export async function fetchTracks(
  siteId: string, from: number, to: number, sample_s = 1, warehouseId?: string
): Promise<AssetTrack[]> {
  const r = await fetch(
    `${BASE}/v1/sites/${encodeURIComponent(siteId)}/history/tracks?from=${from}&to=${to}&sample_s=${sample_s}${whQuery(warehouseId)}`,
    { headers: authHeaders(), credentials: "include" }
  );
  if (!r.ok) throw new Error(`fetchTracks: ${r.status}`);
  return r.json();
}

export async function fetchHeatmap(
  siteId: string, from: number, to: number, cell_m = 1.0, warehouseId?: string
): Promise<HeatmapCell[]> {
  const r = await fetch(
    `${BASE}/v1/sites/${encodeURIComponent(siteId)}/history/heatmap?from=${from}&to=${to}&cell_m=${cell_m}${whQuery(warehouseId)}`,
    { headers: authHeaders(), credentials: "include" }
  );
  if (!r.ok) throw new Error(`fetchHeatmap: ${r.status}`);
  return r.json();
}

export async function fetchOeeRules(
  siteId: string
): Promise<{ shiftStarts: ShiftWindow[] } | null> {
  const r = await fetch(`${BASE}/v1/sites/${encodeURIComponent(siteId)}/oee-rules`, {
    headers: authHeaders(), credentials: "include",
  });
  if (!r.ok) return null;
  const body = await r.json();
  try {
    const shifts: ShiftWindow[] = JSON.parse(body.shiftStarts ?? "[]");
    return { shiftStarts: shifts };
  } catch {
    return null;
  }
}

/** sFT² telemetry — access/security audit row (analytics /auth-audit). */
export interface AuthAuditRow {
  ts_ms: number;
  occurrence_src: string;
  device_id: string;
  asset_id: string;
  driver_uid: string;
  event_kind: string;   // session_start | session_end | auth_denied | lockout
}

/** sFT² telemetry — driving-safety impact row with server-derived severity (analytics /impacts). */
export interface ImpactRow {
  ts_ms: number;
  device_id: string;
  asset_id: string;
  event_kind: string;   // crash | tilt | harsh_accel
  severity: string;     // LEVE | MODERADO | SEVERO (decisions.md#56)
  shock_g?: number;
  net_g?: number;
  pitch?: number;
  roll?: number;
}

// --- Gateway enrollment / quarantine (decisions.md#69) ---
export interface GatewayEnrollment {
  hardwareId: string;
  status: "pending" | "approved" | "revoked";
  assetId: string | null;
  gatewayId: string | null;
  info: string | null;
  firstSeen: string;
  lastSeen: string;
  approvedAt: string | null;
  approvedBy: string | null;
}

export async function listEnrollments(status?: string): Promise<GatewayEnrollment[]> {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return getJson<GatewayEnrollment[]>(`/v1/gateways/enrollments${q}`);
}

export async function approveEnrollment(
  hardwareId: string, assetId: string, gatewayId?: string,
): Promise<GatewayEnrollment> {
  return getJson<GatewayEnrollment>(`/v1/gateways/${encodeURIComponent(hardwareId)}/approve`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ assetId, gatewayId }),
  });
}

export async function revokeEnrollment(hardwareId: string): Promise<GatewayEnrollment> {
  return getJson<GatewayEnrollment>(`/v1/gateways/${encodeURIComponent(hardwareId)}/revoke`, {
    method: "POST",
  });
}

/** Recovery for a reflashed unit (ADR 135): clears the pinned secret and returns the device
 *  to pending — the next enroll from that hardware id re-pins its secret (re-approval required). */
export async function resetEnrollmentSecret(hardwareId: string): Promise<GatewayEnrollment> {
  return getJson<GatewayEnrollment>(`/v1/gateways/${encodeURIComponent(hardwareId)}/reset-secret`, {
    method: "POST",
  });
}

// ── iC3 inventory (slice 1) ──────────────────────────────────────────────────
export interface InventoryRunSummary {
  id: string; name?: string | null; mapName?: string | null; warehouseId: string;
  source: string; startedAt?: string | null; endedAt?: string | null;
  addressScanned?: number | null; status: "imported" | "partial" | "failed"; createdAt: string;
  /** ADR 365: the enrolled asset that pushed the run (device door); null for manual uploads. */
  assetId?: string | null;
  /** The kit's own device id as it appears in its export. */
  kitDeviceId?: string | null;
  /** ADR 383: the WMS snapshot the run is judged against (its reference), and ADR 384: the
   *  optional closing snapshot that brackets the count window. Null = no snapshot yet.
   *  `expectedPinned` = an operator chose them; an automatic binding may move to a nearer
   *  snapshot when one lands, a pinned one never does. */
  expectedImportId?: string | null;
  expectedAsOf?: string | null;
  closingImportId?: string | null;
  closingAsOf?: string | null;
  expectedPinned?: boolean;
}
/** One WMS snapshot of a warehouse's series (ADR 383). `current` = the latest as_of;
 *  `runs` = how many runs read it as their reference. */
export interface ExpectedStockSnapshot {
  importId: string; asOf: string; importedAt: string; importedBy?: string | null;
  fileName: string; rowCount: number; rejectedCount: number; current: boolean; runs: number;
}
export async function listExpectedStockSnapshots(siteId: string, warehouseId: string): Promise<ExpectedStockSnapshot[]> {
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/expected-stock?warehouseId=${encodeURIComponent(warehouseId)}`);
}
/** ADR 383/384: choose the run's snapshots. Either key present PINS the run; both null
 *  hands it back to the automatic rule. Sends only the two keys — the same PATCH also
 *  carries the title and the map link, which a rebind must not touch. */
export async function bindInventoryRunSnapshots(
  siteId: string, runId: string, binding: { expectedImportId: string | null; closingImportId: string | null },
): Promise<InventoryRunSummary> {
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}`, {
    method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(binding),
  });
}
export interface InventoryTopSku { product: string; quantity: number; }
/** One rejected input line (raw line + parser reason) from an import's reject
 *  report (W17). The list is capped server-side at 50; rejectedCount carries
 *  the true total. */
export interface InventoryReject { line: string; reason: string; }
/** addressTotal/coveragePct/skippedCount/skipped (ADR 122, slice 2) are
 *  nullable/absent for slice-1 runs with no resolvable locations master —
 *  mirrors KpiBlock's Coverage.NONE sentinel (backend never fakes a ratio). */
export interface InventoryKpis {
  skus: number; units: number | null; addressesScanned: number;
  rowCount: number; rejectedCount: number; rejects?: InventoryReject[]; topSkus: InventoryTopSku[];
  addressTotal?: number | null;
  coveragePct?: number | null;
  skippedCount?: number | null;
  skipped?: string[];
  /** ADR 372: of addressesScanned, how many have no row in the address master
   *  (the reads happened; the map is incomplete). Null/absent without a master. */
  addressesOutOfMaster?: number | null;
  /** ADR 410: the split of addressesScanned — with at least one counted reading, with
   *  only unidentified readings; the rest were read empty. The three add up. */
  addressesWithStock?: number;
  addressesUnidentified?: number;
}
export interface InventoryScanRow {
  address: string;
  /** The pallet label the kit read — a per-pallet barcode, not a SKU. */
  product: string;
  /** ADR 275: `product` under the warehouse's alias translation — the WMS item
   *  code when an alias maps it, the raw label otherwise. Same contract as
   *  `OccupancyCell.item`. Absent on payloads older than ADR 275. */
  item?: string | null;
  quantity: number | null; mode: number | null;
  status: "counted" | "empty"; scanTs: string | null;
  /** ADR 425 / 428: depth in a double-deep bay, and whether the identity was
   *  inferred from the front pallet rather than read. Absent when not stated. */
  depth?: "front" | "back";
  inferred?: boolean;
}

export async function listInventoryRuns(siteId: string): Promise<InventoryRunSummary[]> {
  return getJson<InventoryRunSummary[]>(`/v1/sites/${encodeURIComponent(siteId)}/inventory/runs`);
}
/** The newest run that can paint the scene for a warehouse (ADR 190 rule, shared
 *  by the occupancy and KPI-badge layers): runs arrive newest-first; skip
 *  `failed` — a failed run has zero rows, and painting from it would read as a
 *  finished empty inventory. */
export function latestPaintableRun(
  runs: InventoryRunSummary[], warehouseId: string,
): InventoryRunSummary | undefined {
  return runs.find((r) => r.warehouseId === warehouseId && r.status !== "failed");
}
/** ADR 198: the runs that paint the scene, newest-first. A user selection (the
 *  LayerPanel run picker) filters the warehouse's paintable runs; no selection —
 *  or one for another warehouse, or one whose runs all vanished — falls back to
 *  the ADR 190 latest-only rule. Never returns a failed run. */
export function selectedPaintableRuns(
  runs: InventoryRunSummary[], warehouseId: string,
  selection: { warehouseId: string; runIds: string[] } | null,
): InventoryRunSummary[] {
  const paintable = runs.filter((r) => r.warehouseId === warehouseId && r.status !== "failed");
  if (selection && selection.warehouseId === warehouseId) {
    const chosen = paintable.filter((r) => selection.runIds.includes(r.id));
    if (chosen.length > 0) return chosen;
  }
  return paintable.slice(0, 1);
}
export async function getInventoryRun(siteId: string, runId: string): Promise<{ run: InventoryRunSummary; kpis: InventoryKpis }> {
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}`);
}
/** Repair rail for the run↔map link (ADR 140): the backend rejects names that
 *  don't match an imported inventory_map, so this can't recreate the silent
 *  join failure it repairs. */
export async function updateInventoryRunMap(siteId: string, runId: string, mapName: string): Promise<InventoryRunSummary> {
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}`, {
    method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mapName }),
  });
}
/** ADR 232: the run's title, free text. Sends ONLY `name` — the same PATCH also
 *  carries the map link, and a rename must not resubmit (or blank) that. An empty
 *  string clears the title back to the id prefix. */
export async function renameInventoryRun(siteId: string, runId: string, name: string): Promise<InventoryRunSummary> {
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}`, {
    method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }),
  });
}
/** ADR 260: remove a run and everything Postgres holds for it. Global-admin only —
 *  the server gates it; the UI hides the affordance so nobody is offered a button that
 *  403s. Hard delete by design: it frees the bundle set-hash, so the same file can be
 *  uploaded again. The raw uploads stay archived in object storage. */
export async function deleteInventoryRun(
  siteId: string, runId: string,
): Promise<{ runId: string; warehouseId: string; scans: number; bundles: number }> {
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}`,
    { method: "DELETE" });
}
export async function getInventoryRunItems(
  siteId: string, runId: string,
  opts: { address?: string; product?: string; status?: string; page?: number; size?: number } = {},
): Promise<{ items: InventoryScanRow[]; page: number; total: number }> {
  const q = new URLSearchParams();
  if (opts.address) q.set("address", opts.address);
  if (opts.product) q.set("product", opts.product);
  if (opts.status) q.set("status", opts.status);
  if (opts.page) q.set("page", String(opts.page));
  if (opts.size) q.set("size", String(opts.size));
  const qs = q.toString() ? `?${q}` : "";
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}/items${qs}`);
}

/** The server caps a page at this many rows (InventoryReadController). */
export const INVENTORY_ITEMS_PAGE = 500;

/** Every row of a run, in server order. The endpoint pages at 100 by default and the
 *  run report used to show that first page as if it were the whole count (ADR 410):
 *  Santa Cruz read "488 visited" over a table of 100 lines. The first page says how
 *  many there are; the rest are fetched together. */
export async function getAllInventoryRunItems(
  siteId: string, runId: string,
): Promise<{ items: InventoryScanRow[]; total: number }> {
  const first = await getInventoryRunItems(siteId, runId, { size: INVENTORY_ITEMS_PAGE });
  const pages = Math.ceil(first.total / INVENTORY_ITEMS_PAGE);
  const rest = await Promise.all(Array.from({ length: Math.max(0, pages - 1) }, (_, i) =>
    getInventoryRunItems(siteId, runId, { page: i + 1, size: INVENTORY_ITEMS_PAGE })));
  return { items: first.items.concat(...rest.map((r) => r.items)), total: first.total };
}
/** Label registry import (ADR 149): the "Material ID" sheet (Material_ID → Item)
 *  becomes the warehouse's pallet-label → item translation for reconciliation. */
export function uploadProductAliases(
  siteId: string, warehouseId: string, file: File,
): Promise<{ imported: number; rejected: number; rejects: InventoryReject[] }> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("warehouseId", warehouseId);
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/product-aliases`, { method: "POST", body: fd });
}

export async function getProductAliasCount(siteId: string, warehouseId: string): Promise<number> {
  const r = await getJson<{ count: number }>(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/product-aliases?warehouseId=${encodeURIComponent(warehouseId)}`);
  return r.count;
}

/** One inventory may span several exported run files (ADR 147) — every file
 *  lands in the same run server-side. */
/** `name` is the run's title (ADR 232) — the backend has always accepted it on
 *  this endpoint; not sending it is why every imported run read as a hex prefix. */
export function uploadInventoryBundle(
  siteId: string, warehouseId: string, files: File | File[], mapName?: string, name?: string,
): Promise<{ runId: string; status: string; addressScanned: number | null; rowCount: number; rejectedCount: number }> {
  const fd = new FormData();
  for (const f of Array.isArray(files) ? files : [files]) fd.append("file", f);
  fd.append("warehouseId", warehouseId);
  if (mapName) fd.append("mapName", mapName);
  if (name) fd.append("name", name);
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/bundles`, { method: "POST", body: fd });
}

/** ADR 150: incremental inventory — a later walk's files append into an existing
 *  run. `added` = rows the append contributed; `duplicates` = rows the run
 *  already had (re-read on the later walk). */
/** ADR 153: the appended files re-scan addresses this run already holds (with
 *  different content) and no policy was chosen — the caller asks the operator
 *  and re-submits with one. Carries the server's count + address sample. */
export class DuplicateAddressesError extends Error {
  constructor(public readonly addresses: number, public readonly sample: string[]) {
    super("duplicate addresses in run");
    this.name = "DuplicateAddressesError";
  }
}
export type DuplicatePolicy = "add_both" | "keep_latest" | "keep_first";

export async function appendInventoryBundle(
  siteId: string, runId: string, files: File | File[], duplicatePolicy?: DuplicatePolicy,
): Promise<{ runId: string; status: string; addressScanned: number | null; added: number; duplicates: number; rejectedCount: number; superseded: number }> {
  const fd = new FormData();
  for (const f of Array.isArray(files) ? files : [files]) fd.append("file", f);
  const qs = duplicatePolicy ? `?duplicatePolicy=${encodeURIComponent(duplicatePolicy)}` : "";
  const path = `/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}/bundles${qs}`;
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include", method: "POST", body: fd, headers: authHeaders(),
  });
  if (res.status === 409) {
    const body = (await res.json().catch(() => null)) as
      { error?: string; addresses?: number; sample?: string[] } | null;
    if (body?.error === "duplicate_addresses")
      throw new DuplicateAddressesError(body.addresses ?? 0, body.sample ?? []);
    throw new Error(body?.error ?? `409 on ${path}`);
  }
  if (!res.ok) throw new Error(await httpErrorMessage(res, path));
  return (await res.json()) as Awaited<ReturnType<typeof appendInventoryBundle>>;
}

/** ADR 153: per-address timeline across the run's uploads. */
/** One reading in an address's timeline. `item` is `product` alias-translated
 *  (ADR 275); absent on payloads older than it. */
export interface InventoryChangeReading {
  product: string; item?: string | null;
  quantity: number | null; itemStatus: "counted" | "empty" | "unidentified"; scanTs: string | null;
}
export interface InventoryChangeGeneration {
  bundleId: string | null; importedAt: string | null; active: boolean;
  readings: InventoryChangeReading[];
}
export interface InventoryAddressChange { address: string; generations: InventoryChangeGeneration[]; }

export async function getInventoryChanges(
  siteId: string, runId: string,
): Promise<{ addresses: InventoryAddressChange[] }> {
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}/changes`);
}

// ── two-pass walk report (ADR 154) ───────────────────────────────────────────
/** One upload's readings = one traverse of one aisle. Mirrors
 *  PassReportService.Pass. `aisle` is null when the upload spans several (it
 *  then joins no comparison); `direction` is "unknown" when the readings carry
 *  no usable position sweep. */
export interface InventoryPass {
  bundleId: string;
  fileName: string;
  aisle: string | null;
  /** Rack level, null when the upload spans several or the scheme has none. */
  level: number | null;
  direction: "forward" | "back" | "unknown";
  startedAt: string | null;
  endedAt: string | null;
  readings: number;
  labels: number;
  emptyReads: number;
}
/** Forward against back over one aisle (`aisle` null = all aisles rolled up).
 *  Mirrors PassReportService.Comparison. estimatedTotalLabels is null when the
 *  passes share no label and the capture-recapture estimator has no footing. */
export interface InventoryPassComparison {
  aisle: string | null;
  /** Rack level; null on the roll-up row and where the scheme has none. */
  level: number | null;
  addressesCompared: number;
  agreeLabel: number;
  agreeEmpty: number;
  onePassOnly: number;
  labelConflict: number;
  agreementPct: number | null;
  forwardLabels: number;
  backLabels: number;
  bothLabels: number;
  unionLabels: number;
  forwardRecallPct: number | null;
  backRecallPct: number | null;
  estimatedTotalLabels: number | null;
  estimatedMissedLabels: number | null;
}
/** A bin the rack-height sensor measured as occupied while no pass read a label
 *  there: stock present, identity unknown. Mirrors
 *  PassReportService.UnidentifiedStock. */
export interface InventoryUnidentifiedStock {
  address: string;
  heightMm: number | null;
  positionHeightMm: number | null;
  /** Null when the height sensor never measured the bin and the finding rests on
   *  an `unidentified` reading alone (ADR 263). Never render null as 0 % — that
   *  reports a confirmed-empty shelf where the camera saw stock. */
  occupancyPct: number | null;
}
/** One bin the iC3 rack-height sensor measured (ADR 162): `loadHeightMm` the load
 *  inside the slot, `binHeightMm` the slot's own clear height, `occupancyPct` the
 *  kit's occupancy (0–100, trusted verbatim). `level` is the Nível from the
 *  address grammar, null off-grammar. Superset of `unidentifiedStock` — every
 *  measured bin, labelled or not. Mirrors PassReportService.OccupancyMeasurement. */
export interface InventoryOccupancyMeasurement {
  address: string;
  level: number | null;
  loadHeightMm: number | null;
  binHeightMm: number | null;
  occupancyPct: number;
  /** ADR 300 — the kit measured the load but reported no occupancy, so `binHeightMm` is the
   *  clear height the TWIN states and `occupancyPct` is the twin's own quotient. Never
   *  presented as the same kind of number as a reading the kit divided itself (ADR 293). */
  derived: boolean;
}
/** The situação of one location between the two counts (ADR 159). */
export type ReconSituation =
  | "agree_label" | "agree_empty" | "one_pass_only" | "conflict"
  | "only_count1" | "only_count2" | "duplicated";
/** One level's single pass placed in count 1 or 2. `duplicated` = the level was
 *  walked once and this same pass fills both counts. Mirrors
 *  PassReportService.CountColumn. */
export interface InventoryCountColumn {
  count: 1 | 2;
  aisle: string;
  /** Rack level (Nível), null where the address scheme carries none. Separate
   *  from `aisle` because a grammar can name both (ADR 253). */
  level: number | null;
  direction: "forward" | "back" | "unknown";
  bundleId: string;
  duplicated: boolean;
  readings: number;
  labels: number;
  emptyReads: number;
}
/** One location read side by side by the two counts. `count1`/`count2`: the
 *  product, `""` when that count found the bin empty, `null` when that count
 *  never reached it. Mirrors PassReportService.ReconRow. */
export interface InventoryReconRow {
  aisle: string;
  address: string;
  count1: string | null;
  count2: string | null;
  situation: ReconSituation;
}
/** The report split into two counts, one pass per level (ADR 159). Mirrors
 *  PassReportService.TwoCounts. */
export interface InventoryTwoCounts {
  columns: InventoryCountColumn[];
  rows: InventoryReconRow[];
  agreeLabel: number;
  agreeEmpty: number;
  onePassOnly: number;
  labelConflict: number;
  onlyCount1: number;
  onlyCount2: number;
  duplicated: number;
}
/** `totals` is null when no aisle was walked in both directions. */
export interface InventoryPassReport {
  passes: InventoryPass[];
  aisles: InventoryPassComparison[];
  totals: InventoryPassComparison | null;
  unidentifiedStock: InventoryUnidentifiedStock[];
  occupancyMeasurements: InventoryOccupancyMeasurement[];
  twoCounts: InventoryTwoCounts;
}

export async function getInventoryPasses(
  siteId: string, runId: string,
): Promise<InventoryPassReport> {
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}/passes`);
}

// ── iC3 × WMS reconciliation (slice 1b, ADR 119) ─────────────────────────────
/** One (address, product) comparison row. expectedQty/countedQty are null on
 *  the side that didn't have the pair (missing_in_ic3 → no countedQty;
 *  unexpected_in_ic3 → no expectedQty) — mirrors DivergenceService.DivRow.
 *  fifoDate/ageDays are the WMS aging enrichment (ADR 194): age at the
 *  snapshot's own asOf; null when the snapshot has no FIFO date or the row is
 *  unexpected_in_ic3 (no WMS side). */
export interface DivergenceRow {
  address: string;
  product: string;
  expectedQty: number | null;
  countedQty: number | null;
  /** ADR 384: `received_in_window` / `shipped_in_window` = pairs the closing snapshot
   *  explains (the WMS itself moved them during the count) — reported, never compared. */
  status: "match" | "qty_mismatch" | "missing_in_ic3" | "unexpected_in_ic3" | "received_in_window" | "shipped_in_window";
  fifoDate: string | null;
  ageDays: number | null;
  /** ADR 410: the WMS shelf-life date and the days left at the snapshot's asOf
   *  (negative = expired). FEFO sites rotate on this; FIFO sites on the receiving
   *  date. Null without an expiry or without a WMS side. */
  expiryDate?: string | null;
  daysToExpiry?: number | null;
  /** ADR 283: review metadata ABOUT the divergence, never a correction of it —
   *  null on match rows and on divergences nobody has justified. `disposition`
   *  (ADR 364) says what happens next. */
  review?: { justification: string; reviewedBy: string; reviewedAt: string; disposition: Disposition } | null;
}
/** ADR 364: accepted = explained, stays as measured · recount = go back to the address
 *  (closes itself when an appended pass re-reads it into a match) · adjust_wms = fix the WMS. */
export type Disposition = "accepted" | "recount" | "adjust_wms";
/** Mirrors DivergenceService.DivSummary. asOf is null when no WMS snapshot has
 *  been imported yet for the run's warehouse (the web tab's empty state). */
export interface DivergenceSummary {
  agreementPct: number;
  matched: number;
  qtyMismatch: number;
  /** False when no reading in the run carried a quantity — then `qtyMismatch` is
   *  NOT MEASURABLE, not zero, and must never render as a bare 0 (ADR 254). */
  quantitiesKnown: boolean;
  missingInIc3: number;
  unexpectedInIc3: number;
  /** ADR 283: divergent rows carrying a review. Outstanding = divergent − justified. */
  justified: number;
  /** ADR 364: of `justified`, how many are marked for a recount / a WMS fix. */
  toRecount: number;
  toAdjustWms: number;
  /** ADR 364: recount reviews whose row has since become a match — closed by a re-read. */
  recountClosed: number;
  ic3Addresses: number;
  wmsAddresses: number;
  coverageVsWmsPct: number;
  /** ADR 374: WMS pairs / addresses the run never visited — set aside, not
   *  compared, not counted as missing. */
  notVisited: number;
  notVisitedAddresses: number;
  asOf: string | null;
  /** ADR 384: pairs the closing snapshot explained — out of the denominator like notVisited. */
  receivedInWindow?: number;
  shippedInWindow?: number;
  /** ADR 383: which snapshots produced this comparison. */
  referenceImportId?: string | null;
  closingImportId?: string | null;
  closingAsOf?: string | null;
  ruleVersion?: number;
}
export interface Divergences {
  summary: DivergenceSummary;
  rows: DivergenceRow[];
}

/**
 * Customer-facing stock report (ADR 241) — one row per address, judged against the
 * WMS balance. Separate from the divergence reconciliation on purpose: this asks
 * whether the balance is borne out by what the camera saw standing at the address,
 * which a run with no quantities can still answer.
 */
export type StockReportOutcome = "OK" | "FOUND" | "NOT_SCANNED" | "MISMATCH";
export interface StockReportRow {
  address: string;
  product: string | null;
  description: string | null;
  fifoDate: string | null;
  expectedQty: number | null;
  /** null = the address was never visited. 0 = it was read and held nothing. */
  countedQty: number | null;
  outcome: StockReportOutcome;
}
export interface StockReport {
  warehouseId: string;
  /** What the report's Warehouse column prints — the warehouse's name, or its id. */
  warehouseLabel: string;
  rows: StockReportRow[];
  totals: Record<StockReportOutcome, number>;
}

export async function getInventoryStockReport(siteId: string, runId: string): Promise<StockReport> {
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}/stock-report`);
}

export async function getInventoryDivergences(siteId: string, runId: string): Promise<Divergences> {
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}/divergences`);
}

/** ADR 283: justify divergences — `all: true` resolves the outstanding set
 *  SERVER-side (a client-sent list would race the computed report); explicit
 *  rows are validated row-by-row and rejects come back named, never silent. */
export async function reviewDivergences(
  siteId: string, runId: string,
  body: { justification: string; rows?: { address: string; product: string }[]; all?: boolean; disposition?: Disposition },
): Promise<{ reviewed: number; rejected: { address: string; product: string; reason: string }[] }> {
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}/divergences/review`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
}

export async function unreviewDivergences(
  siteId: string, runId: string, rows: { address: string; product: string }[],
): Promise<{ removed: number }> {
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/runs/${encodeURIComponent(runId)}/divergences/unreview`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rows }) });
}

/** WMS expected-stock snapshot import — one more entry in the warehouse's series (ADR 383;
 *  it used to replace the prior snapshot). `asOf` is required unless the file carries an
 *  AsOf column: yyyy-MM-dd or yyyy-MM-ddTHH:mm on the site's clock. The old contract note:
 *  warehouse — ADR 119). Same no-Content-Type multipart idiom as
 *  uploadInventoryBundle: the browser sets the boundary itself. */
export function uploadExpectedStock(
  siteId: string, warehouseId: string, file: File, asOf?: string,
): Promise<{ importId: string; duplicate?: boolean; rebound?: number; upserted: number; merged: number; fifoDropped: number; datesDropped: number; skippedNoAddress: number; rejected: number; rejects: InventoryReject[]; asOf: string }> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("warehouseId", warehouseId);
  if (asOf) fd.append("asOf", asOf);
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/expected-stock`, { method: "POST", body: fd });
}

/** WMS movements feed import (ADR 377): the provisional
 *  `Timestamp;Type;Product;Quantity[;Address[;Reference]]` CSV. An import is the
 *  complete record of the days it covers — `supersededRows` counts the daily
 *  rows of earlier imports it replaced; `duplicate` is the same bytes again
 *  (200, nothing written). `from`/`to` are null for a fully rejected file.
 *  Same no-Content-Type multipart idiom as uploadInventoryBundle/uploadExpectedStock. */
export function uploadStockMovements(
  siteId: string, warehouseId: string, file: File,
): Promise<{
  importId: string; duplicate: boolean; imported: number; rejected: number; rejects: InventoryReject[];
  from: string | null; to: string | null; days: number; items: number;
  byType: { pick: number; issue: number; receipt: number; transfer: number; adjust: number };
  rowsWithoutAddress: number; supersededRows: number; timezone: string;
}> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("warehouseId", warehouseId);
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/stock-movements`, { method: "POST", body: fd });
}

// ── iC3 locations master import (slice 2, ADR 122) ───────────────────────────
/** zones.json import (upsert + deactivate-absent, mirrors InventoryLocationController).
 *  Same no-Content-Type multipart idiom as uploadInventoryBundle/uploadExpectedStock.
 *  No mapName field: the map's name lives inside zones.json itself. */
export function uploadLocations(
  siteId: string, warehouseId: string, file: File, aislePattern?: string,
): Promise<{ mapId: string; created: number; updated: number; deactivated: number; unchanged: number; rejects: string[] }> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("warehouseId", warehouseId);
  if (aislePattern) fd.append("aislePattern", aislePattern);
  return getJson(`/v1/sites/${encodeURIComponent(siteId)}/inventory/locations/import`, { method: "POST", body: fd });
}

/** ADR 169: the 2-D ground-plane similarity {tx,ty,theta,scale} that maps a
 *  map's own (kit) frame into the warehouse frame. */
export interface MapFrameTransform { tx: number; ty: number; theta: number; scale: number; }

/** Imported location masters of a site (ADR 139) — feeds the bundle upload's
 *  map-name suggestions so run.mapName actually joins an inventory_map.
 *  `calibrated`/`mapToWarehouse` (ADR 169) expose the map's warehouse-frame
 *  alignment; a `null` transform renders geometry in place. */
export interface InventoryMapRow {
  id: string; name: string; warehouseId: string; locationCount?: number;
  calibrated?: boolean;
  mapToWarehouse?: MapFrameTransform | null;
}
export async function listInventoryMaps(siteId: string): Promise<InventoryMapRow[]> {
  return getJson<InventoryMapRow[]>(`/v1/sites/${encodeURIComponent(siteId)}/inventory/maps`);
}

/** Set (transform) or clear (`null`) a map's warehouse-frame calibration
 *  (ADR 169). The colon-bearing map id rides as a query param. Returns the
 *  updated map row. */
export async function setMapCalibration(
  siteId: string, mapId: string, cal: MapFrameTransform | null,
): Promise<InventoryMapRow> {
  return getJson<InventoryMapRow>(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/maps/calibration?mapId=${encodeURIComponent(mapId)}`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cal ?? {}) },
  );
}

/** Active storage locations of a warehouse (address master) — mirrors
 *  InventoryLocationController.list. Used to join divergence rows to aisles
 *  client-side for the per-aisle reconciliation (ADR 137). */
export interface StorageLocationRow {
  id: string;
  address: string;
  polygon: number[][] | null;
  side: string | null;
  aisleZoneId: string | null;
  aisleLabel: string | null;
  /** Which map's frame the polygon came from — the only way to tell maps apart
   *  under `raw` (ADR 258). */
  mapId?: string;
  /** ADR 190: the RAW enrichment columns (what the operator/import set — not the
   *  effective structure-first resolution the scene renders with). */
  level?: number | null;
  elevationM?: number | null;
  clearHeightM?: number | null;
  /** ADR 302 — what is actually IN FORCE for this address, and which tier supplied it.
   *  `clearHeightM` above stays the RAW column ("what was measured here", often null);
   *  heights resolve at read time and are never copied down, so an empty raw column with
   *  `clearHeightSource: "level"` means the structure page's number is doing its job. */
  effectiveClearHeightM?: number | null;
  clearHeightSource?: "location" | "level" | "nominal" | "default";
  /** ADR 322 — the same pair for the elevation, with one tier the clear height has no
   *  use for: `floor` means the level rests on the slab, which is structure rather than
   *  a guess. `nominal`/`default` mean the base came from `(level − origin) × pitch`. */
  effectiveElevationM?: number | null;
  elevationSource?: "location" | "level" | "floor" | "nominal" | "default" | null;
}

/** `raw` returns each map's own frame instead of the warehouse frame (ADR 258) — the
 *  calibration editor applies a candidate transform itself and must not compose one on
 *  top of a server-side one. */
export async function listStorageLocations(
  siteId: string, warehouseId: string, opts?: { raw?: boolean },
): Promise<StorageLocationRow[]> {
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/locations`
    + `?warehouseId=${encodeURIComponent(warehouseId)}${opts?.raw ? "&raw=true" : ""}`,
  );
}

/** Per-location height edit (ADR 186 tier 2; UI shipped with ADR 190's bin
 *  inspector). Fields present in `patch` are applied, absent ones untouched —
 *  there is no clear-to-null on this rail. The location id carries a literal
 *  pipe, so it rides as a query param (same rule as the calibration PUT). */
export async function patchInventoryLocation(
  siteId: string, locationId: string,
  patch: { elevationM?: number; clearHeightM?: number; level?: number },
): Promise<StorageLocationRow> {
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/locations?locationId=${encodeURIComponent(locationId)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch) },
  );
}

// ── iC3 per-aisle coverage (slice 3, ADR 122) ────────────────────────────────
/** Per-aisle occupancy rollup for a run's resolved map — mirrors
 *  InventoryReadService.AisleCoverage. `aisle` is the raw bucket key
 *  ("__none__" for locations with neither aisleLabel nor aisleZoneId — the
 *  caller maps that literal to a translated label, never displays it raw). */
export interface AisleCoverage {
  aisle: string;
  total: number;
  scanned: number;
  coveragePct: number;
  /** When this aisle's first/last effective reading was taken (ADR 259). Null when
   *  the aisle went unscanned, or when the source dialect carried no timestamps —
   *  an aisle with readings but no clock has no place on the timeline. */
  firstScanMs?: number | null;
  lastScanMs?: number | null;
}

export async function getInventoryAisleCoverage(
  siteId: string, runId: string,
): Promise<{ groupBy: string; mapResolved: boolean; aisles: AisleCoverage[] }> {
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/occupancy?runId=${encodeURIComponent(runId)}&groupBy=aisle`,
  );
}

// ── iC3 3D occupancy (slice 4, ADR 122) ──────────────────────────────────────
/** Re-exported (imported at the top of this file, not redefined) so the API
 *  and the 3D scene layer can't drift — `OccupancyLayer.tsx` is the source of
 *  truth for the shape the occupancy endpoint's `groupBy=location` branch
 *  returns. */
export type { OccupancyCell };

/** Per-location occupancy for a run's WAREHOUSE (`groupBy=location`, the
 *  endpoint's default; warehouse-scoped by ADR 190 — a real inventory spans
 *  every per-level kit map at once). Mirrors `InventoryReadController.
 *  occupancy`'s location branch, which (unlike the aisle branch above) never
 *  sets a `groupBy` key on the response body, hence its absence here.
 *  `mapResolved: false` means the warehouse has no active locations master
 *  yet — `locations` is `[]` in that case, not an error (ADR 123 fixed the
 *  backend so `polygon` arrives as a real array, not a JSON-encoded string). */
export async function getInventoryOccupancy(
  siteId: string, runId: string, runIds?: string[],
): Promise<{ mapResolved: boolean; locations: OccupancyCell[] }> {
  // ADR 198: `runIds` merges several of the warehouse's runs into one paint —
  // per address, the newest selected run that scanned it wins (each cell's
  // `runId` names its source). Omitted for the plain latest-run read.
  const extra = runIds && runIds.length > 1
    ? `&runIds=${runIds.map(encodeURIComponent).join(",")}` : "";
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/occupancy?runId=${encodeURIComponent(runId)}&groupBy=location${extra}`,
  );
}

// ── run movements (ADR 198) ──────────────────────────────────────────────────
/** One pallet label counted at exactly one address in each of two runs, at
 *  different addresses. `item` is the label under the warehouse's alias
 *  translation (the WMS item code where one maps). */
export interface InventoryMovement {
  product: string; item: string; fromAddress: string; toAddress: string;
}
export interface InventoryMovements {
  fromRunId: string; toRunId: string;
  movedCount: number; appearedCount: number; disappearedCount: number; ambiguousCount: number;
  moves: InventoryMovement[];
}
export async function getInventoryMovements(
  siteId: string, fromRunId: string, toRunId: string,
): Promise<InventoryMovements> {
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/movements?fromRunId=${encodeURIComponent(fromRunId)}&toRunId=${encodeURIComponent(toRunId)}`,
  );
}

// ── run-over-run trend (ADR 362) ─────────────────────────────────────────────
/** One run's verdict against one WMS snapshot, pinned at compute time — the
 *  point never moves under a later WMS upload. Mirrors InventoryTrendService.Judgement. */
export interface InventoryTrendJudgement {
  importId: string; asOf: string; computedAt: string;
  matched: number; qtyMismatch: number; quantitiesKnown: boolean;
  missingInIc3: number; unexpectedInIc3: number;
  /** qtyMismatch + missingInIc3 + unexpectedInIc3 */
  divergent: number;
  agreementPct: number; ic3Addresses: number; wmsAddresses: number; coverageVsWmsPct: number;
}
export interface InventoryTrendPoint {
  runId: string; name: string | null; startedAt: string | null; endedAt: string | null;
  status: "imported" | "partial"; addressesScanned: number | null;
  /** ADR 410: read addresses over the active master, the run report's own number;
   *  null without a master. Not the WMS-relative figure in the judgement. */
  coveragePct?: number | null;
  /** null when the warehouse has no WMS snapshot (nothing to judge against). */
  judgement: InventoryTrendJudgement | null;
}
export type ChronicStatus = "qty_mismatch" | "missing_in_ic3" | "unexpected_in_ic3";
/** `hits` of `runsConsidered` judged counts had this address divergent. */
export interface InventoryChronicAddress {
  address: string; hits: number; runsConsidered: number;
  lastStatus: ChronicStatus; lastProduct: string; lastRunId: string;
}
export interface InventoryTrend {
  warehouseId: string;
  /** Oldest first. */
  runs: InventoryTrendPoint[];
  chronic: InventoryChronicAddress[];
  chronicWindow: number; chronicMinHits: number;
  wmsSnapshot: boolean;
}
export async function getInventoryTrend(
  siteId: string, warehouseId: string, limit?: number,
): Promise<InventoryTrend> {
  const q = limit ? `&limit=${limit}` : "";
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/trend?warehouseId=${encodeURIComponent(warehouseId)}${q}`);
}

// ── product search catalog (ADR 196) ─────────────────────────────────────────
/** One distinct WMS item of the warehouse's current expected-stock snapshot.
 *  `description` is the WMS Description column, null when the export had none. */
export interface ProductCatalogRow {
  product: string;
  description: string | null;
}

/** Empty list — not an error — when no WMS snapshot was ever imported; the
 *  /live product search then still matches on the cells' scanned labels. */
export async function getInventoryProducts(
  siteId: string, warehouseId: string,
): Promise<ProductCatalogRow[]> {
  return getJson(
    `/v1/sites/${encodeURIComponent(siteId)}/inventory/products?warehouseId=${encodeURIComponent(warehouseId)}`,
  );
}

export const api = {
  /** Access & security audit trail over device_events (fusion §8.2). */
  authAudit(siteId: string, fromMs: number, toMs: number, driverId?: string) {
    const d = driverId ? `&driverId=${encodeURIComponent(driverId)}` : "";
    return getJson<AuthAuditRow[]>(
      `/v1/sites/${encodeURIComponent(siteId)}/auth-audit?from=${fromMs}&to=${toMs}${d}`);
  },
  /** Driving-safety impacts with derived severity (fusion §8.3). */
  impacts(siteId: string, fromMs: number, toMs: number, severityMin?: string) {
    const s = severityMin ? `&severityMin=${encodeURIComponent(severityMin)}` : "";
    return getJson<ImpactRow[]>(
      `/v1/sites/${encodeURIComponent(siteId)}/impacts?from=${fromMs}&to=${toMs}${s}`);
  },
  listAssets(siteId: string, warehouseId?: string) {
    return getJson<Asset[]>(`/v1/sites/${encodeURIComponent(siteId)}/assets${whQuery(warehouseId, "?")}`);
  },
  /** Seed the V2Pro SLAM localization to a known map-frame pose. angle is RADIANS,
   *  same frame/sign as getPose.yaw (decisions.md#25, #47). */
  relocate(assetId: string, p: { x: number; y: number; angle: number }) {
    return getJson<Record<string, unknown>>(
      `/v1/forklifts/${encodeURIComponent(assetId)}/v2pro/relocate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...p, noisyX: 0, noisyY: 0, noisyAngle: 0 }),
      });
  },
  listAssetTypes(siteId: string) {
    return getJson<AssetType[]>(`/v1/sites/${encodeURIComponent(siteId)}/asset-types`);
  },
  createAssetType(siteId: string, t: { typeCode: string; label: string; description?: string }) {
    return getJson<AssetType>(`/v1/sites/${encodeURIComponent(siteId)}/asset-types`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(t),
    });
  },
  updateAssetType(siteId: string, typeCode: string,
                  t: { label?: string; description?: string; modelScale?: number; hourCost?: number | null }) {
    return getJson<AssetType>(`/v1/sites/${encodeURIComponent(siteId)}/asset-types/${encodeURIComponent(typeCode)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(t),
    });
  },
  async deleteAssetType(siteId: string, typeCode: string): Promise<void> {
    const res = await fetch(`${BASE}/v1/sites/${encodeURIComponent(siteId)}/asset-types/${encodeURIComponent(typeCode)}`, {
      method: "DELETE",
      headers: authHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  },
  // warehouseId optional: the server resolves the site's single warehouse and
  // rejects when ambiguous (explicit-warehouse rule, retrospective C5).
  createAsset(siteId: string, a: { label: string; typeCode: string; warehouseId?: string; model?: string; active?: boolean; gatewayBaseUrl?: string }) {
    return getJson<Asset>(`/v1/sites/${encodeURIComponent(siteId)}/assets`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(a),
    });
  },
  updateAsset(siteId: string, id: string, p: Partial<Pick<Asset, "label" | "model" | "typeCode" | "modelUrl" | "active" | "warehouseId" | "gatewayBaseUrl" | "commandTransport">> & { edgeConfig?: unknown }) {
    return getJson<Asset>(`/v1/sites/${encodeURIComponent(siteId)}/assets/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(p),
    });
  },
  async deleteAsset(siteId: string, id: string): Promise<void> {
    const res = await fetch(`${BASE}/v1/sites/${encodeURIComponent(siteId)}/assets/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: authHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  },
  /** Capability-graded OEE (decisions.md#56/#61): absent factors are OMITTED, never 0. */
  getOeeModular(siteId: string, from: Date, to: Date) {
    return getJson<ModularOee>(
      `/v1/sites/${encodeURIComponent(siteId)}/oee-modular?from=${from.getTime()}&to=${to.getTime()}`
    );
  },
  listAlerts(siteId: string, status: "open" | "ack" | "resolved" = "open") {
    return getJson<AlertApiRecord[]>(`/v1/sites/${encodeURIComponent(siteId)}/alerts?status=${status}`);
  },
  getZones(siteId: string, warehouseId?: string) {
    return getJson<unknown[]>(`/v1/sites/${encodeURIComponent(siteId)}/zones${whQuery(warehouseId, "?")}`);
  },
  getOeeRange(siteId: string, from: Date, to: Date, warehouseId?: string) {
    const f = from.toISOString();
    const t = to.toISOString();
    return getJson<OeeWindowRow[]>(`/v1/sites/${encodeURIComponent(siteId)}/oee?from=${encodeURIComponent(f)}&to=${encodeURIComponent(t)}${whQuery(warehouseId)}`);
  },
  getBufferHeatmap(siteId: string, hours = 1, cell_m = 2, warehouseId?: string) {
    return getJson<BufferCell[]>(
      `/v1/sites/${encodeURIComponent(siteId)}/network/buffer-heatmap?cell_m=${cell_m}&hours=${hours}${whQuery(warehouseId)}`
    );
  },
  getNetworkConnectivity(siteId: string, hours = 8, warehouseId?: string) {
    return getJson<ConnData>(
      `/v1/sites/${encodeURIComponent(siteId)}/network/connectivity?hours=${hours}${whQuery(warehouseId)}`
    );
  },
  getDisruptions(siteId: string, hours = 24, limit = 5, warehouseId?: string) {
    return getJson<Disruption[]>(
      `/v1/sites/${encodeURIComponent(siteId)}/network/disruptions?hours=${hours}&limit=${limit}${whQuery(warehouseId)}`
    );
  },
  listSensors(siteId: string, warehouseId?: string) {
    return getJson<SensorInstall[]>(`/v1/sites/${encodeURIComponent(siteId)}/sensors${whQuery(warehouseId, "?")}`);
  },
  createSensor(siteId: string, s: Omit<SensorInstall, "id" | "siteId"> & { warehouseId: string }) {
    return getJson<SensorInstall>(`/v1/sites/${encodeURIComponent(siteId)}/sensors`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(s),
    });
  },
  updateSensor(siteId: string, id: string, s: Partial<Omit<SensorInstall, "id" | "siteId">>) {
    return getJson<SensorInstall>(`/v1/sites/${encodeURIComponent(siteId)}/sensors/${encodeURIComponent(id)}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(s),
    });
  },
  async deleteSensor(siteId: string, id: string): Promise<void> {
    const res = await fetch(`${BASE}/v1/sites/${encodeURIComponent(siteId)}/sensors/${encodeURIComponent(id)}`,
      { method: "DELETE", headers: authHeaders(), credentials: "include" });
    if (!res.ok && res.status !== 404) throw new Error(`deleteSensor: ${res.status}`);
  },
  ackAlert(alertId: string) {
    return fetch(`${BASE}/v1/alerts/${encodeURIComponent(alertId)}/ack`,
      { method: "POST", headers: authHeaders(), credentials: "include" });
  },
};

// ── run trail + kit events (ADR 259) ─────────────────────────────────────────
/** One decimated pose of the run's walk, in the MAP frame — place it with the
 *  map's own `mapToWarehouse` (the transform the alignment editor writes). */
export interface TrailPoint {
  bucket_ts: number;
  x: number;
  y: number;
  yaw: number;
  /** The sink writes the kit's localized flag as 1.0/0.0, so 0 means the kit did
   *  not trust its own pose there. */
  confidence: number;
  samples: number;
}

export interface RunTrail {
  points: TrailPoint[];
  mapName: string | null;
  assetId: string | null;
  /** False for a manual upload, or a push that predates the run's asset column:
   *  the trail is then warehouse-scoped, and the events are unavailable. */
  assetKnown: boolean;
  fromMs: number;
  toMs: number;
  bucketMs: number;
}

export interface KitEvent {
  ts: number;
  kind: string;
  deviceId: string;
  payload: string;
}

export interface RunEvents {
  events: KitEvent[];
  assetId: string | null;
  assetKnown: boolean;
  fromMs: number;
  toMs: number;
}

function runTelemetryUrl(siteId: string, warehouseId: string, runId: string, leaf: string): string {
  return `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}`
    + `/inventory/runs/${encodeURIComponent(runId)}/${leaf}`;
}

export async function getRunTrail(
  siteId: string, warehouseId: string, runId: string,
): Promise<RunTrail> {
  return getJson<RunTrail>(runTelemetryUrl(siteId, warehouseId, runId, "trail"));
}

export async function getRunEvents(
  siteId: string, warehouseId: string, runId: string,
): Promise<RunEvents> {
  return getJson<RunEvents>(runTelemetryUrl(siteId, warehouseId, runId, "events"));
}

/**
 * One recorded user action (ADR 267). `detail` is raw JSON text — the shape
 * differs per action, and re-typing it per action here would be a second place
 * to keep in step with the writers.
 */
export interface AuditEventRow {
  id: string;
  ts: string;
  actorSub: string | null;
  actorName: string | null;
  service: string;
  action: string;
  method: string | null;
  endpoint: string | null;
  entityType: string;
  entityId: string;
  siteId: string | null;
  orgUnitId: string | null;
  outcome: "ok" | "denied" | "error";
  status: number | null;
  durationMs: number | null;
  error: string | null;
  detail: string | null;
}

/** The trail, newest first. Global admin only — a non-admin gets 403, and the
 *  caller shows that rather than an empty list, because "you may not see this"
 *  and "nothing has happened" are different answers. */
export async function listAuditTrail(siteId?: string, limit = 200): Promise<AuditEventRow[]> {
  const q = new URLSearchParams({ limit: String(limit) });
  if (siteId) q.set("siteId", siteId);
  const r = await fetch(`${BASE}/v1/audit-trail?${q}`, { headers: authHeaders(), credentials: "include" });
  if (!r.ok) throw new Error(`audit-trail ${r.status}`);
  return r.json();
}

/** Everything that ever happened to one entity — the query that still answers
 *  after the entity itself has been deleted. */
export async function listAuditForEntity(entityType: string, entityId: string): Promise<AuditEventRow[]> {
  const r = await fetch(
    `${BASE}/v1/audit-trail/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
    { headers: authHeaders(), credentials: "include" });
  if (!r.ok) throw new Error(`audit-trail ${r.status}`);
  return r.json();
}

// ── slot photos (ADR 268/269/272) ────────────────────────────────────────────
/** A photograph of what stands at one address. The kit v6 fires one frame per
 *  product↔address association (ADR 270); everything here already works for the
 *  frames uploaded by hand today. */
export interface SlotPhotoRow {
  id: string;
  address: string;
  runId: string | null;
  takenAt: string | null;
  /** How the address is known — `kit_association` is the only one an auditor can
   *  take at face value; `manual` means a human typed it. */
  addressSource: "kit_association" | "manual" | "label_barcode" | "label_ocr" | "filename";
  scanId: string | null;
  chosen: boolean;
  chosenReason: "only_frame" | "auto" | "operator" | null;
  contentType: string;
  byteSize: number;
  widthPx: number | null;
  heightPx: number | null;
  exifOrientation: number | null;
  note: string | null;
  uploadedAt: string;
}

function slotPhotoBase(siteId: string, warehouseId: string): string {
  return `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/slot-photos`;
}

/** Photos for one address, or for a whole run. Both filters are optional and the
 *  unfiltered form is the warehouse's whole set — fine for the counts a report
 *  needs, deliberately not paged until a real kit fills it. */
export async function listSlotPhotos(
  siteId: string, warehouseId: string, opts: { address?: string; runId?: string } = {},
): Promise<SlotPhotoRow[]> {
  const q = new URLSearchParams();
  if (opts.address) q.set("address", opts.address);
  if (opts.runId) q.set("runId", opts.runId);
  const qs = q.toString();
  return getJson<SlotPhotoRow[]>(`${slotPhotoBase(siteId, warehouseId)}${qs ? `?${qs}` : ""}`);
}

/** The path an authenticated fetch reads the bytes from. NOT usable as an
 *  `<img src>`: the endpoint is tenant-scoped and an img tag cannot carry a
 *  bearer token (ADR 204) — go through {@link fetchSlotPhotoObjectUrl}. */
export function slotPhotoImagePath(siteId: string, warehouseId: string, id: string): string {
  return `${slotPhotoBase(siteId, warehouseId)}/${encodeURIComponent(id)}/image`;
}

/** Fetch the bytes and wrap them in an object URL. The caller owns the URL and
 *  must revoke it — an un-revoked blob holds a multi-megabyte original alive for
 *  the life of the document. */
export async function fetchSlotPhotoObjectUrl(
  siteId: string, warehouseId: string, id: string,
): Promise<string> {
  const path = slotPhotoImagePath(siteId, warehouseId, id);
  const res = await fetch(`${BASE}${path}`, { headers: authHeaders(), credentials: "include" });
  if (!res.ok) throw new Error(await httpErrorMessage(res, path));
  return URL.createObjectURL(await res.blob());
}

/**
 * Attach a photograph to one address by hand (ADR 296) — the same door the kit's edge
 * addon pushes through, taken by a person with a phone.
 *
 * `addressSource` is left unset on purpose so the server stamps its `manual` default
 * (ADR 269): a human who typed the address has not read it off the label, and the
 * evidence column must not claim otherwise. `runId` binds the frame to the count the
 * operator is looking at when there is one; without it the photo still belongs to the
 * address, which is what the association is.
 */
export async function uploadSlotPhoto(
  siteId: string, warehouseId: string,
  opts: { file: File; address: string; runId?: string | null; note?: string },
): Promise<SlotPhotoRow> {
  const form = new FormData();
  form.append("file", opts.file);
  form.append("address", opts.address);
  if (opts.runId) form.append("runId", opts.runId);
  if (opts.note?.trim()) form.append("note", opts.note.trim());
  const path = slotPhotoBase(siteId, warehouseId);
  // No Content-Type header: the browser has to set the multipart boundary itself.
  const res = await fetch(`${BASE}${path}`, {
    method: "POST", headers: authHeaders(), credentials: "include", body: form,
  });
  if (!res.ok) throw new Error(await httpErrorMessage(res, path));
  return res.json() as Promise<SlotPhotoRow>;
}

/** Promote one frame to be the evidence for its address (ADR 269). Recorded as
 *  `operator`, never `auto` — a reviewer has to be able to tell a judgement from
 *  a default. */
export async function chooseSlotPhoto(
  siteId: string, warehouseId: string, id: string,
): Promise<SlotPhotoRow> {
  return getJson<SlotPhotoRow>(
    `${slotPhotoBase(siteId, warehouseId)}/${encodeURIComponent(id)}/chosen`, { method: "PUT" });
}

// ── aisle photos (ADR 398) ───────────────────────────────────────────────────
/** A picture of an aisle — reference, not evidence: no run, no address. Keyed on the
 *  aisle's structure node, which is what a derived aisle zone carries (`zone.nodeId`). */
export interface AislePhotoRow {
  id: string;
  nodeId: string;
  takenAt: string | null;
  contentType: string;
  byteSize: number;
  widthPx: number | null;
  heightPx: number | null;
  exifOrientation: number | null;
  note: string | null;
  uploadedAt: string;
}

function aislePhotoBase(siteId: string, warehouseId: string): string {
  return `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/aisle-photos`;
}

export async function listAislePhotos(
  siteId: string, warehouseId: string, nodeId: string,
): Promise<AislePhotoRow[]> {
  return getJson<AislePhotoRow[]>(`${aislePhotoBase(siteId, warehouseId)}?nodeId=${encodeURIComponent(nodeId)}`);
}

/** Bytes behind a tenant-scoped endpoint, as an object URL the caller must revoke —
 *  same reason as {@link fetchSlotPhotoObjectUrl}: an `<img src>` carries no token. */
export async function fetchAislePhotoObjectUrl(
  siteId: string, warehouseId: string, id: string,
): Promise<string> {
  const path = `${aislePhotoBase(siteId, warehouseId)}/${encodeURIComponent(id)}/image`;
  const res = await fetch(`${BASE}${path}`, { headers: authHeaders(), credentials: "include" });
  if (!res.ok) throw new Error(await httpErrorMessage(res, path));
  return URL.createObjectURL(await res.blob());
}

export async function uploadAislePhoto(
  siteId: string, warehouseId: string, opts: { file: File; nodeId: string; note?: string },
): Promise<AislePhotoRow> {
  const form = new FormData();
  form.append("file", opts.file);
  form.append("nodeId", opts.nodeId);
  if (opts.note?.trim()) form.append("note", opts.note.trim());
  const path = aislePhotoBase(siteId, warehouseId);
  // No Content-Type header: the browser has to set the multipart boundary itself.
  const res = await fetch(`${BASE}${path}`, {
    method: "POST", headers: authHeaders(), credentials: "include", body: form,
  });
  if (!res.ok) throw new Error(await httpErrorMessage(res, path));
  return res.json() as Promise<AislePhotoRow>;
}

export async function deleteAislePhoto(siteId: string, warehouseId: string, id: string): Promise<void> {
  const path = `${aislePhotoBase(siteId, warehouseId)}/${encodeURIComponent(id)}`;
  const res = await fetch(`${BASE}${path}`, { method: "DELETE", headers: authHeaders(), credentials: "include" });
  if (!res.ok) throw new Error(await httpErrorMessage(res, path));
}

// ── slot point clouds (ADR 285/286) ──────────────────────────────────────────
/** A stitched dual-camera capture of what stands at one address, shipped by the
 *  kit's edge addon through the evidence rail (docs/ic3-evidence-contract.md). */
export interface SlotCloudRow {
  id: string;
  address: string;
  runId: string | null;
  captureId: string | null;
  side: "left" | "right" | null;
  capturedAt: string | null;
  pointCount: number | null;
  stitchSource: "seed_icp" | "seed_only" | "single" | null;
  stitchResidualMm: number | null;
  byteSize: number;
  uploadedAt: string;
  note: string | null;
}

function slotCloudBase(siteId: string, warehouseId: string): string {
  return `/v1/sites/${encodeURIComponent(siteId)}/warehouses/${encodeURIComponent(warehouseId)}/slot-clouds`;
}

/** Clouds for one address, newest first (mirrors {@link listSlotPhotos}). */
export async function listSlotClouds(
  siteId: string, warehouseId: string, opts: { address?: string; runId?: string } = {},
): Promise<SlotCloudRow[]> {
  const q = new URLSearchParams();
  if (opts.address) q.set("address", opts.address);
  if (opts.runId) q.set("runId", opts.runId);
  const qs = q.toString();
  return getJson<SlotCloudRow[]>(`${slotCloudBase(siteId, warehouseId)}${qs ? `?${qs}` : ""}`);
}

/** Fetch the raw PCD bytes of one cloud. Tenant-scoped like the photo bytes
 *  (ADR 204: the auth header travels with the fetch, never a bare URL). */
export async function fetchSlotCloudBuffer(
  siteId: string, warehouseId: string, id: string,
): Promise<ArrayBuffer> {
  const path = `${slotCloudBase(siteId, warehouseId)}/${encodeURIComponent(id)}/cloud`;
  const res = await fetch(`${BASE}${path}`, { headers: authHeaders(), credentials: "include" });
  if (!res.ok) throw new Error(await httpErrorMessage(res, path));
  return res.arrayBuffer();
}

// ── blocker mask ────────────────────────────────────────────────────────────

/** The world frame of a warehouse's blocker mask (ADR 343, V68). */
export interface BlockerMaskMeta {
  warehouseId: string;
  /** World metres of the frame's low corner. */
  originX: number;
  originY: number;
  /** Metres per cell — 0,25 by default, matching the heat field's grid. */
  cellM: number;
  widthPx: number;
  heightPx: number;
  hasPainted: boolean;
}

function blockerMaskBase(siteId: string, warehouseId: string): string {
  return `/v1/sites/${encodeURIComponent(siteId)}`
       + `/warehouses/${encodeURIComponent(warehouseId)}/blocker-mask`;
}

/**
 * URL of the COMPOSED mask PNG — derived rack layer OR the painted one, merged
 * server-side so no two clients can disagree about what is blocked.
 *
 * This is for AUTHENTICATED fetches only: it carries no token of its own, so a
 * plain {@code <img src>} both 401s on every secured deployment (there is no
 * way for an `<img>` to send {@link authHeaders}) and 404s whenever the
 * warehouse has no mask, which is the normal state for most warehouses. Use
 * {@link getBlockerMaskPng} for the normal case; a loader that genuinely needs
 * to hand the browser a URL (an `<img>`/texture loader, not `fetch`) should
 * follow the pattern `WarehouseModel.tsx:40` uses for {@code useGLTF} —
 * fetching the bytes itself and injecting the header via `gltfWithAuth`.
 *
 * Pass `version` after re-uploading a painted layer — the object key is stable,
 * so without a cache-buster the browser keeps serving the previous mask.
 */
export function blockerMaskUrl(siteId: string, warehouseId: string,
                               version?: number): string {
  const base = `${BASE}${blockerMaskBase(siteId, warehouseId)}`;
  return version == null ? base : `${base}?v=${version}`;
}

/**
 * Fetches the composed mask PNG with the bearer token attached, returning
 * null when this warehouse has no mask (the normal state for most
 * warehouses) rather than throwing.
 */
export async function getBlockerMaskPng(
  siteId: string, warehouseId: string, version?: number,
): Promise<Blob | null> {
  const path = blockerMaskBase(siteId, warehouseId) + (version == null ? "" : `?v=${version}`);
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await httpErrorMessage(res, "blocker-mask"));
  return res.blob();
}

/** The mask's frame, or null when this warehouse has none — the normal state. */
export async function getBlockerMaskMeta(
  siteId: string, warehouseId: string,
): Promise<BlockerMaskMeta | null> {
  const res = await fetch(`${BASE}${blockerMaskBase(siteId, warehouseId)}/meta`, {
    credentials: "include",
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await httpErrorMessage(res, "blocker-mask/meta"));
  return (await res.json()) as BlockerMaskMeta;
}

/** Create the mask, sizing its frame from the warehouse's own geometry. */
export async function createBlockerMask(
  siteId: string, warehouseId: string,
): Promise<BlockerMaskMeta> {
  return getJson<BlockerMaskMeta>(blockerMaskBase(siteId, warehouseId), {
    method: "POST",
  });
}

/** Replace the hand-painted layer. Its dimensions must match the stored frame. */
export async function putPaintedMask(
  siteId: string, warehouseId: string, png: Blob,
): Promise<void> {
  const body = new FormData();
  body.append("file", png, "painted.png");
  const res = await fetch(`${BASE}${blockerMaskBase(siteId, warehouseId)}/painted`, {
    method: "PUT",
    credentials: "include",
    headers: authHeaders(),   // no Content-Type: the browser sets the boundary
    body,
  });
  if (!res.ok) throw new Error(await httpErrorMessage(res, "blocker-mask/painted"));
}

/** Drop the painted layer, keeping the mask and its frame. */
export async function deletePaintedMask(
  siteId: string, warehouseId: string,
): Promise<void> {
  const res = await fetch(`${BASE}${blockerMaskBase(siteId, warehouseId)}/painted`, {
    method: "DELETE",
    credentials: "include",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await httpErrorMessage(res, "blocker-mask/painted"));
}

// ── Assistant exchanges (ADR 412) ────────────────────────────────────────────

/** One question put to the assistant and how it ended. `no_engine` rows are the
 *  knowledge base the next engines are built from; the others are the eval set. */
export type AssistantExchangeOutcome = "answered" | "no_engine" | "refusal" | "error";
export interface AssistantExchangeRow {
  id: string;
  siteId: string;
  warehouseId: string | null;
  runId: string | null;
  page: string | null;
  subject: string | null;
  askedAt: string;
  question: string;
  answer: string | null;
  tools: string[];
  outcome: AssistantExchangeOutcome;
  gapTopic: string | null;
  gapNeed: string | null;
  model: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
}

/** Newest first. Global admin only — a non-admin gets 403, shown as a refusal,
 *  not as an empty list (same rule as the audit trail). */
export async function listAssistantExchanges(opts: {
  outcome?: AssistantExchangeOutcome; siteId?: string; limit?: number;
} = {}): Promise<AssistantExchangeRow[]> {
  const q = new URLSearchParams({ limit: String(opts.limit ?? 100) });
  if (opts.outcome) q.set("outcome", opts.outcome);
  if (opts.siteId) q.set("siteId", opts.siteId);
  const r = await fetch(`${BASE}/v1/assistant/exchanges?${q}`, { headers: authHeaders(), credentials: "include" });
  if (!r.ok) throw new Error(`assistant/exchanges ${r.status}`);
  return r.json();
}
